import { useCallback, useEffect, useRef, useState } from "react";
import { loadAdminVPScript } from "../../../utils/adminVersaPay";
import { growFrameWhenRecaptchaClicked, stopRecaptchaGrow } from "../../../utils/versapayFrame";
import { getAdminTransactions, getAdminDealStages, processAdminRefund, getAdminDealGiftCard, updateAdminContact, getAdminContact, getAdminContactDeals, updateAdminDeal, getAdminDealLineItems, changeAdminPackage, getProducts, createVersapaySession, confirmVersapayPayment, logAuditEvent, enrollContactInWorkflow, AuditChange } from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";
import { HUBSPOT_IDS, hubspotRecordUrl } from "../../../utils/hubspotIds";

type Transaction = {
  dealId: string;
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  ghinNumber: string;
  vipCardNumber: string;
  transactionNumber: string;
  orderNumber: string;
  dealName: string;
  amount: string;
  amountRaw: number;
  vipPackage: string;
  vipCardType: string;
  dealstage: string;
  createDate: string;
  paymentMethod: string;
  checkNumber: string;
  miscNote?: string;
  isManual: boolean;
  ownerName: string;
  isBagTagOnly: boolean;
  hasBagTag: boolean;
  hasDonation: boolean;
  isReplacementCard: boolean;
  isWvVipCard: boolean;
  isBronzeAddon: boolean;
  usedGiftCard: boolean;
  ghinError: string;
  createdSource: string;
  netsuiteId: string;
  netsuiteCreditMemoId: string;
  packageChange: string; // "Upgrade" | "Downgrade" | ""
  promoCode: string;
  promoAmount: number;
};

type DealStage = { id: string; label: string };

type LineItem = { id: string; name: string; price: number; quantity: number; amount: number; dealId?: string };

// A Bronze Add-On row loads its VIP deal too; each deal is refunded or changed against its own transaction.
type LinkedDeal = { dealId: string; dealName: string; amount: number; transactionNumber: string; paymentMethod: string; netsuiteId?: string; role: "vip" | "addon" };

// Package change from a Bronze Add-On row: VIP deal + add-on are treated as Bronze and the VIP deal is changed.
type ChangeLink = { vip: LinkedDeal; addon: LinkedDeal; addonPkgName: string; addonPkgPrice: number };

// Mirrors the server: a downgrade refunds the add-on's transaction first, then the VIP deal's.
const splitAddonRefund = (diff: number, addonAmount: number) => {
  const total = diff < 0 ? -diff : 0;
  const fromAddon = Math.min(total, addonAmount);
  return { fromAddon, fromVip: Math.round((total - fromAddon) * 100) / 100 };
};
const isCardPaid = (d: LinkedDeal) => d.paymentMethod.toLowerCase().trim() === "credit card" && !!d.transactionNumber;

type RefundState =
  | { status: "idle" }
  | { status: "loading";    transaction: Transaction }
  | { status: "confirm";    transaction: Transaction; lineItems: LineItem[]; selected: Set<string>; currentPackage: string }
  | { status: "processing"; transaction: Transaction; lineItems: LineItem[]; selected: Set<string>; currentPackage: string }
  | { status: "success";    refundId: string | null;  transaction: Transaction }
  | { status: "error";      message: string;          transaction: Transaction; lineItems: LineItem[]; selected: Set<string>; currentPackage: string };

type VipProduct = { id: string; name: string; label: string; price: number; product_id: string; tabKey: string; };

type ChangeState =
  | { status: "idle" }
  | { status: "loading";    transaction: Transaction }
  | { status: "select";     transaction: Transaction; lineItems: LineItem[]; products: VipProduct[]; oldLineItem: LineItem | null; selectedProductId: string; currentPackage: string }
  | { status: "confirm";    transaction: Transaction; lineItems: LineItem[]; products: VipProduct[]; oldLineItem: LineItem | null; selectedProduct: VipProduct; diff: number; paymentMethod: string; checkNumber: string; miscNote: string; vpRefTxId: string; currentPackage: string }
  | { status: "processing"; transaction: Transaction; selectedProduct: VipProduct; diff: number }
  | { status: "success";    transaction: Transaction; selectedProduct: VipProduct; diff: number }
  | { status: "error";      message: string; transaction: Transaction; lineItems: LineItem[]; products: VipProduct[]; oldLineItem: LineItem | null; selectedProduct: VipProduct; diff: number; paymentMethod: string; checkNumber: string; miscNote: string; vpRefTxId: string; currentPackage: string };

const toLocalDateStr = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatDateTime = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const BRAND_GREEN = "#023822";
const BRAND_GOLD  = "#C9A24D";

const stageBadge = (dealstage: string, stages: DealStage[]) => {
  const isRefunded        = dealstage === HUBSPOT_IDS.refundedStage;
  const isPartialRefunded = dealstage === HUBSPOT_IDS.partialRefundStage;
  const label = isRefunded        ? "Refunded"
              : isPartialRefunded ? "Partial Refund"
              : stages.find((s) => s.id === dealstage)?.label || "—";
  const dotColor = isRefunded        ? "#dc3545"
                 : isPartialRefunded ? "#f59e0b"
                 : "#22c55e";
  return (
    <span title={label} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: dotColor,
      }} />
    </span>
  );
};

const AllTransactions = () => {
  const today = toLocalDateStr(new Date());

  const [startDate,      setStartDate]      = useState<string>("");
  const [endDate,        setEndDate]        = useState<string>("");
  const [dealstage,      setDealstage]      = useState<string>("");   // set after stages load
  const [dealStages,     setDealStages]     = useState<DealStage[]>([]);
  const [stagesLoaded,   setStagesLoaded]   = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [search,      setSearch]      = useState("");
  const [cardTypeFilter, setCardTypeFilter] = useState<"" | "digital" | "paper">("");
  const [sortCol, setSortCol] = useState<"name" | "vipCardType" | "vipPackage" | "paymentMethod" | "createDate" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // Pagination
  const [pageSize, setPageSize] = useState<20 | 50 | 100>(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Refund state machine
  const [refund, setRefund] = useState<RefundState>({ status: "idle" });
  const [refundDeals, setRefundDeals] = useState<LinkedDeal[] | null>(null);
  // Deals refunded this visit; HubSpot search can lag a few seconds behind, so hide their Refund button regardless
  const [refundedDealIds, setRefundedDealIds] = useState<Set<string>>(new Set());
  const refundInputRef = useRef<HTMLInputElement>(null);

  // Change package state machine
  const [changePkg, setChangePkg] = useState<ChangeState>({ status: "idle" });
  const [changeLink, setChangeLink] = useState<ChangeLink | null>(null);
  const changeLinkRef = useRef(changeLink);
  useEffect(() => { changeLinkRef.current = changeLink; }, [changeLink]);

  // VersaPay refs for the change-package modal
  const vpContainerRef    = useRef<HTMLDivElement>(null);
  const vpClientRef       = useRef<any>(null);
  const vpSessionIdRef    = useRef<string>("");
  const shrinkVpFrame = () => {
    stopRecaptchaGrow();
    const iframe = vpContainerRef.current?.querySelector("iframe");
    if (iframe) iframe.style.height = "270px";
  };
  const vpSessionAmtRef   = useRef<number>(0);
  const vpInFlightRef     = useRef(false);
  const vpMountedRef      = useRef(false);
  const vpFrameRef        = useRef(false);
  const [vpLoading,     setVpLoading]     = useState(false);
  const [vpError,       setVpError]       = useState<string | null>(null);
  const [vpSubmitting,  setVpSubmitting]  = useState(false);
  const vpContactAddressRef = useRef<{ address1: string; city: string; state: string; zip: string }>({ address1: "", city: "", state: "", zip: "" });

  // Keep a ref to changePkg for stable approval callback
  const changePkgRef = useRef(changePkg);
  useEffect(() => { changePkgRef.current = changePkg; }, [changePkg]);

  // Which deal the package change is applied to, plus the add-on details when changing from a Bronze Add-On row.
  const changeTarget = (t: Transaction, link: ChangeLink | null, diff: number, paymentMethod: string) => {
    if (!link) return { dealId: t.dealId, currentDealAmount: t.amountRaw };
    return {
      dealId:             link.vip.dealId,
      currentDealAmount:  link.vip.amount,
      addonDealId:        link.addon.dealId,
      addonDealAmount:    link.addon.amount,
      addonPackageName:   link.addonPkgName,
      addonPackagePrice:  link.addonPkgPrice,
      addonTransactionId: diff < 0 && paymentMethod === "credit_card" && isCardPaid(link.addon) ? link.addon.transactionNumber : undefined,
    };
  };

  const applyPackageChangeToRows = (t: Transaction, link: ChangeLink | null, newLabel: string, diff: number, newTxId?: string) => {
    if (!link) {
      setTransactions((prev) => prev.map((row) => row.dealId !== t.dealId ? row : {
        ...row,
        vipPackage: newLabel,
        packageChange: diff > 0 ? "Upgrade" : diff < 0 ? "Downgrade" : row.packageChange,
        amountRaw:  Math.max(0, t.amountRaw + diff),
        amount:     `$${Math.max(0, t.amountRaw + diff).toFixed(2)}`,
        ...(newTxId ? { transactionNumber: newTxId } : {}),
      }));
      return;
    }
    const { fromAddon, fromVip } = splitAddonRefund(diff, link.addon.amount);
    const vipAmount   = Math.max(0, link.vip.amount + (diff > 0 ? diff : -fromVip));
    const addonAmount = Math.max(0, link.addon.amount - fromAddon);
    setTransactions((prev) => prev.map((row) => {
      if (row.dealId === link.vip.dealId) {
        return { ...row, vipPackage: newLabel, packageChange: diff > 0 ? "Upgrade" : diff < 0 ? "Downgrade" : row.packageChange, amountRaw: vipAmount, amount: `$${vipAmount.toFixed(2)}`, ...(newTxId ? { transactionNumber: newTxId } : {}) };
      }
      if (row.dealId === link.addon.dealId) {
        return { ...row, amountRaw: addonAmount, amount: `$${addonAmount.toFixed(2)}` };
      }
      return row;
    }));
  };

  // VersaPay approval handler — fires when customer submits card in iframe
  const handleVpApproval = useCallback(async (result: any, sessionId: string) => {
    const cp      = changePkgRef.current;
    const billing = vpContactAddressRef.current;
    if (cp.status !== "confirm") return;

    setChangePkg({ status: "processing", transaction: cp.transaction, selectedProduct: cp.selectedProduct, diff: cp.diff });

    try {
      // 1. Confirm VersaPay charge for the upgrade difference
      const confirmResp = await confirmVersapayPayment({
        sessionId,
        approvalResult: result,
        amount:    Math.round(cp.diff * 100),
        firstName: cp.transaction.firstName,
        lastName:  cp.transaction.lastName,
        email:     cp.transaction.email,
        address1:  billing.address1 || undefined,
        city:      billing.city     || undefined,
        state:     billing.state    || undefined,
        zip:       billing.zip      || undefined,
        lineItems: [{
          type: "Item", number: "UPGRADE",
          description: `Upgrade to ${cp.selectedProduct.name}`,
          price: Math.round(cp.diff * 100), quantity: 1, discount: 0,
        }],
      } as any);

      if (!confirmResp?.success) throw new Error(confirmResp?.error || "Payment confirmation failed.");

      // 2. Apply the package change in HubSpot
      const link = changeLinkRef.current;
      const changeResp = await changeAdminPackage({
        ...changeTarget(cp.transaction, link, cp.diff, "credit_card"),
        oldLineItemId:     cp.oldLineItem?.id,
        oldPackageName:    cp.oldLineItem?.name || cp.transaction.vipPackage,
        oldPackagePrice:   cp.oldLineItem?.price ?? 0,
        promoReversal:     promoReversalFor((cp as any).lineItems ?? [], cp.oldLineItem ?? null),
        newProductId:      cp.selectedProduct.product_id,
        newPackageName:    cp.selectedProduct.name,
        newPackageTabKey:  cp.selectedProduct.tabKey,
        newPackagePrice:   cp.selectedProduct.price,
        diff:              cp.diff,
        paymentMethod:     "credit_card",
        versapayTransactionId: confirmResp.transactionId,
        versapayVpOrderNumber: confirmResp.vpOrderNumber,
      });

      if (!changeResp.success) throw new Error(changeResp.message || "Package change failed.");

      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "package_change",
        label:   "Package Upgrade (Credit Card)",
        details: `${cp.transaction.dealName} (+$${cp.diff.toFixed(2)})`,
        changes: [
          { field: "Package", oldValue: (cp as any).currentPackage || "?", newValue: cp.selectedProduct.label },
          { field: "Amount",  oldValue: `$${(cp.transaction.amountRaw).toFixed(2)}`, newValue: `$${(cp.transaction.amountRaw + cp.diff).toFixed(2)}` },
        ],
      });
      applyPackageChangeToRows(cp.transaction, link, cp.selectedProduct.label, cp.diff);
      setChangePkg({ status: "success", transaction: cp.transaction, selectedProduct: cp.selectedProduct, diff: cp.diff });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "An error occurred.";
      const cp2 = changePkgRef.current;
      if (cp2.status === "processing") {
        // Go back to confirm so user can retry
        setChangePkg({
          status: "error", message: msg,
          transaction: cp.transaction, lineItems: (cp as any).lineItems ?? [],
          products: (cp as any).products ?? [], oldLineItem: cp.oldLineItem ?? null,
          selectedProduct: cp.selectedProduct, diff: cp.diff,
          paymentMethod: "credit_card", checkNumber: "", miscNote: "",
          vpRefTxId: (cp as any).vpRefTxId ?? cp.transaction.transactionNumber ?? "",
          currentPackage: (cp as any).currentPackage ?? "",
        });
      }
    }
  }, []);

  // VersaPay setup/teardown effect
  const vpStatus   = changePkg.status;
  const vpMethod   = changePkg.status === "confirm" ? changePkg.paymentMethod : null;
  const vpDiffAmt  = (changePkg.status === "confirm" || changePkg.status === "processing" || changePkg.status === "success" || changePkg.status === "error") ? changePkg.diff : 0;
  const vpTxEmail  = changePkg.status === "confirm" ? changePkg.transaction.email : "";
  const vpTxFirst  = changePkg.status === "confirm" ? changePkg.transaction.firstName : "";
  const vpTxLast   = changePkg.status === "confirm" ? changePkg.transaction.lastName : "";

  useEffect(() => {
    const active = vpStatus === "confirm" && vpMethod === "credit_card" && vpDiffAmt > 0;
    if (!active) {
      vpMountedRef.current  = false;
      vpFrameRef.current    = false;
      vpInFlightRef.current = false;
      vpSessionIdRef.current  = "";
      vpSessionAmtRef.current = 0;
      stopRecaptchaGrow();
      setVpLoading(false);
      setVpError(null);
      setVpSubmitting(false);
      return;
    }
    vpMountedRef.current = true;
    const amtCents = Math.round(vpDiffAmt * 100);

    const setup = async () => {
      if (vpInFlightRef.current) return;
      // Form already mounted for this payment with the right amount
      if (vpFrameRef.current && vpSessionIdRef.current && vpSessionAmtRef.current === amtCents) return;
      vpInFlightRef.current = true;
      setVpLoading(true);
      setVpError(null);
      try {
        // Sessions are single-use, so each card form gets a new one
        const sr = await createVersapaySession({ amount: amtCents, firstName: vpTxFirst, lastName: vpTxLast, email: vpTxEmail });
        if (!sr?.sessionId) throw new Error("No session ID.");
        const sid: string = sr.sessionId;
        vpSessionIdRef.current  = sid;
        vpSessionAmtRef.current = amtCents;
        vpFrameRef.current = false;
        if (!vpMountedRef.current || !vpContainerRef.current) return;

        await loadAdminVPScript();
        if (!vpMountedRef.current || !vpContainerRef.current) return;
        if (!(window as any).versapay) throw new Error("VersaPay SDK not loaded.");

        const client = await (window as any).versapay.initClient(sid);
        vpClientRef.current = client;
        if (!vpMountedRef.current || !vpContainerRef.current) return;

        if (client.onApproval) {
          const boundSid = sid;
          client.onApproval(
            (res: any) => { shrinkVpFrame(); handleVpApproval(res, boundSid); },
            (err: any) => {
              shrinkVpFrame();
              setVpError(err?.error || err?.message || "Payment declined. Please try again.");
            }
          );
        }

        if (!vpFrameRef.current) {
          vpContainerRef.current.innerHTML = "";
          await client.initFrame(vpContainerRef.current, "100%", "270px");
          if (!vpMountedRef.current || !vpContainerRef.current) return;
          const iframe = vpContainerRef.current.querySelector("iframe");
          if (iframe) { iframe.style.width = "100%"; iframe.style.height = "270px"; }
          vpFrameRef.current = true;
        }
      } catch (err: any) {
        if (vpMountedRef.current) setVpError(err?.message || "Failed to load payment form.");
        vpFrameRef.current = false;
      } finally {
        vpInFlightRef.current = false;
        if (vpMountedRef.current) setVpLoading(false);
      }
    };
    setup();
    return () => { vpMountedRef.current = false; };
  }, [vpStatus, vpMethod, vpDiffAmt, vpTxEmail, vpTxFirst, vpTxLast, handleVpApproval]);

  // Keywords that positively identify a line item as the VIP membership package.
  // Prefer positive matching over exclusion-only so non-package items (donations, bag tags, gifts)
  // can never accidentally be returned as the "current package".
  const PKG_LINE_KEYWORDS = ["bronze", "silver", "gold", "wv vip", "vip card", "vip package", " package"];
  // "VSGA-VIP: General Scholarship Donations" contains "vip", so name matching alone can pick the donation
  const NON_PACKAGE_LINE = /donation|scholarship|foundation|bag tag|member gift|promo code|gift card|^refund\s*-/i;
  const isPackageLine = (li: LineItem) => li.quantity > 0 && !NON_PACKAGE_LINE.test(li.name);
  const findPackageLineItem = (items: LineItem[]): LineItem | null =>
    items.find((li) =>
      isPackageLine(li) &&
      PKG_LINE_KEYWORDS.some((k) => li.name.toLowerCase().includes(k))
    ) || null;

  // Edit contact modal
  type EditForm = { firstName: string; lastName: string; email: string; phone: string; date_of_birth: string; sex: string; address: string; street_address_2: string; city: string; state: string; zip: string; vipCardType: string; ghinNumber: string; };
  type DealHistoryItem = { dealId: string; dealName: string; amount: string; stageLabel: string; dealstage: string; vipPackage: string; vipCardType: string; paymentMethod: string; transactionNumber: string; checkNumber: string; miscNote: string; orderNumber: string; createDate: string; isBagTagOnly: boolean; isReplacementCard: boolean; isWvVipCard: boolean; isBronzeAddon: boolean; hasBagTag: boolean; hasDonation: boolean; netsuiteId: string; netsuiteCreditMemoId: string; isVipPipeline: boolean; };

  const BLANK_FORM: EditForm = { firstName: "", lastName: "", email: "", phone: "", date_of_birth: "", sex: "", address: "", street_address_2: "", city: "", state: "", zip: "", vipCardType: "", ghinNumber: "" };

  const [editModal, setEditModal] = useState<{
    open: boolean; contactId: string; dealId: string; tab: "personal" | "address" | "history" | "netsuite";
    form: EditForm; initialForm: EditForm; loadingContact: boolean; saving: boolean; error: string | null; success: boolean;
    deals: DealHistoryItem[]; loadingDeals: boolean; dealsError: string | null; dealsLoaded: boolean;
    transaction: Transaction | null; isActiveCurrentYear: boolean; membershipYear: string;
  }>({
    open: false, contactId: "", dealId: "", tab: "personal",
    form: BLANK_FORM, initialForm: BLANK_FORM, loadingContact: false, saving: false, error: null, success: false,
    deals: [], loadingDeals: false, dealsError: null, dealsLoaded: false,
    transaction: null, isActiveCurrentYear: false, membershipYear: "",
  });

  const openEditModal = async (t: Transaction) => {
    setEditModal({
      open: true, contactId: t.contactId, dealId: t.dealId, tab: "personal",
      form: BLANK_FORM, initialForm: BLANK_FORM, loadingContact: true, saving: false, error: null, success: false,
      deals: [], loadingDeals: false, dealsError: null, dealsLoaded: false,
      transaction: t, isActiveCurrentYear: false, membershipYear: "",
    });
    try {
      const resp = await getAdminContact(t.contactId);
      if (resp.success && resp.contact) {
        const loaded = { ...resp.contact, vipCardType: t.vipCardType || "", ghinNumber: (resp.contact as any).ghinNumber || "" };
        const isActiveCurrentYear = !!(resp.contact as any).isActiveCurrentYear;
        const membershipYear = String((resp.contact as any).membershipYear || "");
        setEditModal((prev) => ({ ...prev, loadingContact: false, form: loaded, initialForm: loaded, isActiveCurrentYear, membershipYear }));
      } else {
        setEditModal((prev) => ({ ...prev, loadingContact: false, error: resp.message || "Failed to load contact." }));
      }
    } catch {
      setEditModal((prev) => ({ ...prev, loadingContact: false, error: "Network error loading contact." }));
    }
  };

  const switchTab = async (tab: "personal" | "address" | "history" | "netsuite") => {
    setEditModal((prev) => ({ ...prev, tab }));
    if ((tab === "history" || tab === "netsuite") && !editModal.dealsLoaded) {
      setEditModal((prev) => ({ ...prev, loadingDeals: true, dealsError: null }));
      try {
        const resp = await getAdminContactDeals(editModal.contactId);
        if (resp.success) {
          setEditModal((prev) => ({ ...prev, loadingDeals: false, deals: resp.deals || [], dealsLoaded: true }));
        } else {
          setEditModal((prev) => ({ ...prev, loadingDeals: false, dealsError: resp.message || "Failed to load deals.", dealsLoaded: true }));
        }
      } catch {
        setEditModal((prev) => ({ ...prev, loadingDeals: false, dealsError: "Network error.", dealsLoaded: true }));
      }
    }
  };

  const [cardTypeEditing, setCardTypeEditing] = useState<string | null>(null); // dealId being edited
  const [cardTypeSaving, setCardTypeSaving] = useState<string | null>(null);   // dealId being saved

  const saveCardType = async (dealId: string, value: string) => {
    setCardTypeSaving(dealId);
    try {
      const resp = await updateAdminDeal(dealId, { vip_card_type: value });
      if (resp.success) {
        setEditModal((prev) => ({
          ...prev,
          deals: prev.deals.map((d) => d.dealId === dealId ? { ...d, vipCardType: value } : d),
        }));
      }
    } finally {
      setCardTypeSaving(null);
      setCardTypeEditing(null);
    }
  };

  const closeEditModal = () => setEditModal((prev) => ({ ...prev, open: false, error: null, success: false }));

  const saveEditModal = async () => {
    if (editModal.saving) return;
    setEditModal((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const hasGhinError = !!(editModal.transaction?.ghinError);
      const resp = await updateAdminContact({ contactId: editModal.contactId, dealId: editModal.dealId, ...editModal.form, clearGhinError: hasGhinError });
      if (resp.success) {
        const FIELD_LABELS: Partial<Record<keyof typeof editModal.form, string>> = {
          firstName: "First Name", lastName: "Last Name", email: "Email", phone: "Phone",
          date_of_birth: "Date of Birth", sex: "Gender", vipCardType: "Card Type",
          address: "Address", street_address_2: "Address 2", city: "City", state: "State", zip: "Zip",
          ghinNumber: "GHIN Number",
        };
        const contactChanges: AuditChange[] = (Object.keys(FIELD_LABELS) as (keyof typeof editModal.form)[])
          .filter((k) => (editModal.initialForm[k] || "") !== (editModal.form[k] || ""))
          .map((k) => ({ field: FIELD_LABELS[k]!, oldValue: editModal.initialForm[k] || "—", newValue: editModal.form[k] || "—" }));
        logAuditEvent({
          actor:   getAdminSession()?.displayName || "Admin",
          action:  "contact_update",
          label:   "Contact Updated",
          details: `${editModal.form.firstName} ${editModal.form.lastName} (${editModal.form.email})`,
          changes: contactChanges.length > 0 ? contactChanges : undefined,
        });
        setTransactions((prev) => prev.map((t) =>
          t.dealId === editModal.dealId
            ? { ...t, firstName: editModal.form.firstName, lastName: editModal.form.lastName, email: editModal.form.email, vipCardType: editModal.form.vipCardType, ghinNumber: editModal.form.ghinNumber || t.ghinNumber, ...(hasGhinError ? { ghinError: "" } : {}) }
            : t.contactId === editModal.contactId
            ? { ...t, firstName: editModal.form.firstName, lastName: editModal.form.lastName, email: editModal.form.email, ghinNumber: editModal.form.ghinNumber || t.ghinNumber, ...(hasGhinError ? { ghinError: "" } : {}) }
            : t
        ));
        setEditModal((prev) => ({ ...prev, saving: false, success: true }));
      } else {
        setEditModal((prev) => ({ ...prev, saving: false, error: resp.message || "Save failed." }));
      }
    } catch (err: any) {
      setEditModal((prev) => ({ ...prev, saving: false, error: err?.response?.data?.message || err?.message || "Network error." }));
    }
  };

  const US_STATES = [
    { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" }, { value: "AZ", label: "Arizona" },
    { value: "AR", label: "Arkansas" }, { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
    { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" }, { value: "FL", label: "Florida" },
    { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
    { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
    { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
    { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
    { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
    { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
    { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
    { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
    { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
    { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
    { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
    { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
    { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
    { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" }, { value: "DC", label: "District of Columbia" },
  ];

  // Gift card modal
  type GiftCardDetail = { id: string; promoCode: string; value: string; valueRaw: number; status: string; type: string; timesUsed?: string; createdAt: string };
  const [gcModal, setGcModal] = useState<{ open: boolean; loading: boolean; data: GiftCardDetail | null; error: string | null }>({
    open: false, loading: false, data: null, error: null,
  });
  const [enrollingIds, setEnrollingIds] = useState<Set<string>>(new Set());

  const handleEnrollWorkflow = async (t: Transaction) => {
    if (!t.email) return;
    setEnrollingIds((prev) => new Set(prev).add(t.contactId));
    try {
      const resp = await enrollContactInWorkflow(t.contactId);
      if (!resp.success) throw new Error(resp.message || "Failed to enroll.");
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "GHIN Re-enroll",
        details: `Re-enrolled ${t.firstName} ${t.lastName} (${t.email}) in GHIN workflow`,
      });
      setTransactions((prev) => prev.map((tx) => tx.contactId === t.contactId ? { ...tx, ghinError: "reprocess" } : tx));
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || "Failed to re-enroll.");
    } finally {
      setEnrollingIds((prev) => { const next = new Set(prev); next.delete(t.contactId); return next; });
    }
  };

  const openGcModal = async (dealId: string) => {
    setGcModal({ open: true, loading: true, data: null, error: null });
    try {
      const resp = await getAdminDealGiftCard(dealId);
      if (resp.success) {
        setGcModal({ open: true, loading: false, data: resp.giftCard, error: null });
      } else {
        setGcModal({ open: true, loading: false, data: null, error: resp.message || "Failed to load gift card." });
      }
    } catch {
      setGcModal({ open: true, loading: false, data: null, error: "Network error." });
    }
  };

  const closeGcModal = () => setGcModal({ open: false, loading: false, data: null, error: null });

  const fetchTransactions = async (start: string, end: string, stage: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await getAdminTransactions(start, end, stage || undefined);
      if (resp.success) {
        setTransactions(resp.transactions);
      } else {
        setError(resp.message || "Failed to load transactions.");
        setTransactions([]);
      }
    } catch {
      setError("Network error. Please try again.");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Load deal stages once on mount, default to purchased stage
  useEffect(() => {
    getAdminDealStages()
      .then((r) => {
        if (r.success) {
          setDealStages(r.stages);
          setDealstage(r.defaultStage);
        }
        setStagesLoaded(true);
      })
      .catch(() => setStagesLoaded(true));
  }, []);

  // Fetch transactions only after the default stage is known
  useEffect(() => {
    if (!stagesLoaded) return;
    fetchTransactions(startDate, endDate, dealstage);
  }, [startDate, endDate, dealstage, stagesLoaded]);

  useEffect(() => {
    if (refund.status === "confirm" && refundInputRef.current) {
      refundInputRef.current.focus();
    }
  }, [refund.status]);

  const filtered = transactions.filter((t) => {
    if (cardTypeFilter && (t.vipCardType || "").toLowerCase() !== cardTypeFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      `${t.firstName} ${t.lastName}`.toLowerCase().includes(q) ||
      (t.firstName || "").toLowerCase().includes(q) ||
      (t.lastName || "").toLowerCase().includes(q) ||
      (t.email || "").toLowerCase().includes(q) ||
      (t.ghinNumber || "").toLowerCase().includes(q) ||
      (t.vipCardNumber || "").toLowerCase().includes(q) ||
      (t.amountRaw > 0 && (t.transactionNumber || "").toLowerCase().includes(q)) ||
      (t.checkNumber || "").toLowerCase().includes(q)
    );
  });

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
    setCurrentPage(1);
  };

  const sorted = [...filtered].sort((a, b) => {
    if (!sortCol) return 0;
    let av = "", bv = "";
    if (sortCol === "name")          { av = `${a.firstName} ${a.lastName}`.toLowerCase(); bv = `${b.firstName} ${b.lastName}`.toLowerCase(); }
    if (sortCol === "vipCardType")   { av = (a.vipCardType || "").toLowerCase(); bv = (b.vipCardType || "").toLowerCase(); }
    if (sortCol === "vipPackage")    { av = (a.vipPackage || a.dealName || "").toLowerCase(); bv = (b.vipPackage || b.dealName || "").toLowerCase(); }
    if (sortCol === "paymentMethod") { av = (a.paymentMethod || "").toLowerCase(); bv = (b.paymentMethod || "").toLowerCase(); }
    if (sortCol === "createDate")    { av = a.createDate; bv = b.createDate; }
    return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const paginated  = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Reset to page 1 when filters change
  useEffect(() => { setCurrentPage(1); }, [cardTypeFilter]);
  useEffect(() => { setCurrentPage(1); }, [search, transactions, pageSize]);

  const openRefundModal = async (t: Transaction) => {
    setRefund({ status: "loading", transaction: t });
    setRefundDeals(null);
    try {
      const resp = await getAdminDealLineItems(t.dealId);
      const lineItems: LineItem[] = resp.lineItems || [];
      const currentPackage: string = resp.currentPackage || "";
      setRefundDeals(resp.linkedDeals?.length ? resp.linkedDeals : null);
      setRefund({ status: "confirm", transaction: t, lineItems, selected: new Set(), currentPackage });
    } catch {
      // Fallback: no line items, show full amount as a single unchecked item
      const fallback: LineItem[] = [{ id: "total", name: t.dealName || "Total", price: t.amountRaw, quantity: 1, amount: t.amountRaw }];
      setRefund({ status: "confirm", transaction: t, lineItems: fallback, selected: new Set(), currentPackage: t.vipPackage || "" });
    }
  };

  const closeRefundModal = () => {
    const refreshList = refund.status === "success";
    setRefund({ status: "idle" });
    setRefundDeals(null);
    if (refreshList) fetchTransactions(startDate, endDate, dealstage);
  };

  const toggleLineItem = (id: string) => {
    if (refund.status !== "confirm" && refund.status !== "error") return;
    const next = new Set(refund.selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setRefund({ ...refund, selected: next });
  };

  // Promo lines (negative) that haven't been reversed by an earlier refund
  const activePromoLines = (items: LineItem[]) => items.filter((li) =>
    /^promo code:/i.test(li.name) && li.amount < 0 &&
    !items.some((r) => r.name.toLowerCase() === `refund - ${li.name.toLowerCase()}` && (r.dealId || "") === (li.dealId || "")));
  // An admin promo still discounting this package. It only covered the original purchase, so on an
  // upgrade/downgrade the customer is credited what they actually paid and the new package is full price.
  const promoOnPackage = (items: LineItem[], pkg: LineItem | null): LineItem | null =>
    pkg ? activePromoLines(items).find((p) => (p.dealId || "") === (pkg.dealId || "")) || null : null;
  const packageBasis = (items: LineItem[], pkg: LineItem | null, link: ChangeLink | null) =>
    (pkg?.price ?? 0) - Math.abs(promoOnPackage(items, pkg)?.amount ?? 0) + (link?.addonPkgPrice ?? 0);
  const promoReversalFor = (items: LineItem[], pkg: LineItem | null) => {
    const promo = promoOnPackage(items, pkg);
    return promo ? { name: promo.name, amount: Math.abs(promo.amount) } : undefined;
  };
  // The promo discounted the package, so it comes off the refund whenever that deal's package is refunded
  const withPromo = (items: LineItem[], selected: Set<string>): LineItem[] => {
    const chosen = items.filter((l) => selected.has(l.id));
    const promos = activePromoLines(items).filter((p) =>
      chosen.some((ch) => isPackageLine(ch) && (ch.dealId || "") === (p.dealId || "")));
    return [...chosen, ...promos];
  };

  const selectedTotal = (refund.status === "confirm" || refund.status === "processing" || refund.status === "error")
    ? withPromo(refund.lineItems || [], refund.selected).reduce((s, l) => s + l.amount, 0)
    : 0;

  // Selected items split by the deal they belong to, each capped at what that deal was paid.
  type RefundGroup = LinkedDeal & { items: LineItem[]; refundAmount: number };
  const buildRefundGroups = (t: Transaction, items: LineItem[], selected: Set<string>): RefundGroup[] => {
    const chosen = withPromo(items, selected);
    const deals: LinkedDeal[] = refundDeals ?? [{
      dealId: t.dealId, dealName: t.dealName, amount: t.amountRaw,
      transactionNumber: t.transactionNumber, paymentMethod: t.paymentMethod, role: "vip",
    }];
    return deals
      .map((d) => {
        const dealItems = refundDeals ? chosen.filter((l) => l.dealId === d.dealId) : chosen;
        const sum = dealItems.reduce((s, l) => s + l.amount, 0);
        return { ...d, items: dealItems, refundAmount: Math.round(Math.min(sum, d.amount) * 100) / 100 };
      })
      .filter((g) => g.items.length > 0 && g.refundAmount > 0);
  };
  const refundGroups = (refund.status === "confirm" || refund.status === "processing" || refund.status === "error")
    ? buildRefundGroups(refund.transaction, refund.lineItems, refund.selected)
    : [];

  const submitRefund = async () => {
    if (refund.status !== "confirm") return;
    const { transaction, lineItems, selected, currentPackage } = refund;
    const groups = buildRefundGroups(transaction, lineItems, selected);
    if (groups.length === 0) return;

    const missingTx = groups.find((g) => g.paymentMethod.toLowerCase().trim() === "credit card" && !g.transactionNumber);
    if (missingTx) {
      setRefund({ status: "error", message: `${missingTx.dealName} was paid by credit card but has no transaction number, so it can't be refunded here.`, transaction, lineItems, selected, currentPackage });
      return;
    }

    setRefund({ status: "processing", transaction, lineItems, selected, currentPackage });

    const refundIds: string[] = [];
    const done = new Set<string>();
    for (const g of groups) {
      try {
        const isPartialRefund = Math.round(g.refundAmount * 100) < Math.round(g.amount * 100);
        const resp = await processAdminRefund({
          transactionId: g.transactionNumber,
          amount: g.refundAmount,
          dealId: g.dealId,
          isPartialRefund,
          paymentMethod: g.paymentMethod,
          lineItems: g.items.map((l) => ({ id: l.id, name: l.name, amount: l.amount, quantity: l.quantity })),
        });
        if (!resp.success) throw new Error(resp.message || "Refund failed.");
        if (resp.refundId) refundIds.push(resp.refundId);
        done.add(g.dealId);
        setRefundedDealIds((prev) => new Set(prev).add(g.dealId));
        applyRefundToRow(g, isPartialRefund);
        logAuditEvent({
          actor:   getAdminSession()?.displayName || "Admin",
          action:  "refund",
          label:   isPartialRefund ? "Partial Refund" : "Full Refund",
          details: `$${g.refundAmount.toFixed(2)} refunded for ${g.dealName} (${g.transactionNumber || g.dealId})`,
        });
      } catch (err: any) {
        const reason = err?.response?.data?.message || err?.message || "Network error.";
        const doneNames = groups.filter((x) => done.has(x.dealId)).map((x) => x.dealName);
        // Drop items from deals already refunded so "Try Again" can't refund them twice.
        const remaining = lineItems.filter((l) => !l.dealId || !done.has(l.dealId));
        const remainingSelected = new Set(Array.from(selected).filter((id) => remaining.some((l) => l.id === id)));
        if (refundDeals) setRefundDeals(refundDeals.filter((d) => !done.has(d.dealId)));
        setRefund({
          status: "error",
          message: doneNames.length
            ? `${doneNames.join(", ")} refunded. ${g.dealName} failed: ${reason}`
            : reason,
          transaction, lineItems: remaining, selected: remainingSelected, currentPackage,
        });
        return;
      }
    }
    setRefund({ status: "success", refundId: refundIds.length ? refundIds.join(", ") : null, transaction });
  };

  const applyRefundToRow = (g: RefundGroup, isPartialRefund: boolean) => {
    if (!isPartialRefund) {
      setTransactions((prev) => prev.filter((t) => t.dealId !== g.dealId));
      return;
    }
    const refundedNames = g.items.map((l) => l.name.toLowerCase());
    const refundedBagTag   = refundedNames.some((n) => n.includes("bag tag"));
    const refundedDonation = refundedNames.some((n) => n.includes("donation") || n.includes("scholarship") || n.includes("foundation"));
    const refundedBronze   = refundedNames.some((n) => n.includes("bronze"));
    const refundedPackage  = refundedNames.some((n) => !n.includes("bag tag") && !n.includes("donation") && !n.includes("scholarship") && !n.includes("foundation"));
    const newAmountRaw = Math.max(0, g.amount - g.refundAmount);
    setTransactions((prev) => prev.map((t) => {
      if (t.dealId !== g.dealId) return t;
      return {
        ...t,
        dealstage:     HUBSPOT_IDS.partialRefundStage,
        amountRaw:     newAmountRaw,
        amount:        `$${newAmountRaw.toFixed(2)}`,
        hasBagTag:     refundedBagTag   ? false : t.hasBagTag,
        hasDonation:   refundedDonation ? false : t.hasDonation,
        isBronzeAddon: refundedBronze   ? false : t.isBronzeAddon,
        vipPackage:    refundedPackage  ? ""    : t.vipPackage,
      };
    }));
  };

  const refundTransaction = refund.status !== "idle" ? refund.transaction : null;
  const modalOpen = refund.status !== "idle";
  const effectiveRefundTotal = refundGroups.reduce((s, g) => s + g.refundAmount, 0);

  const canSubmitRefund =
    (refund.status === "confirm") &&
    refund.selected.size > 0 &&
    effectiveRefundTotal > 0;

  // ── Change Package handlers ──────────────────────────────────────────────────
  const openChangeModal = async (t: Transaction) => {
    setChangePkg({ status: "loading", transaction: t });
    setChangeLink(null);
    // Reset contact address
    vpContactAddressRef.current = { address1: "", city: "", state: "", zip: "" };
    try {
      const [liResp, prodRaw, contactResp] = await Promise.all([
        getAdminDealLineItems(t.dealId),
        getProducts(),
        t.contactId ? getAdminContact(t.contactId) : Promise.resolve({ success: false }),
      ]);
      const lineItems: LineItem[] = liResp.lineItems || [];
      const currentPackage: string = (liResp.currentPackage || "").toLowerCase().trim();
      // Only include genuine VIP membership package products — exclude add-ons, bag tags,
      // donations, gifts, etc. so they never appear in the Change Package dropdown.
      const NON_PKG_KEYS = ["addon", "bagtag", "bag-tag", "bag_tag", "donation", "gift", "scholarship", "foundation"];
      const products: VipProduct[] = (JSON.parse(prodRaw || "[]") as any[])
        .filter((p) => {
          if (!p.product_id || !(p.price > 0)) return false;
          const tab = (p.tabKey || "").toLowerCase();
          return !NON_PKG_KEYS.some((k) => tab.includes(k));
        })
        .map((p) => { const raw = (p.label || p.name || "").replace(/\s+package$/i, "").trim(); return { id: p.id, name: p.name, label: raw, price: Number(p.price), product_id: p.product_id, tabKey: p.tabKey || "" }; });
      const vipDeal   = liResp.linkedDeals?.find((d) => d.role === "vip");
      const addonDeal = liResp.linkedDeals?.find((d) => d.role === "addon");
      // A Bronze Add-On change is applied to the VIP deal, which must be in NetSuite too
      if (vipDeal && addonDeal && !vipDeal.netsuiteId) {
        setChangePkg({ status: "error", message: `The linked VIP deal (${vipDeal.dealName}) isn't in NetSuite yet, so the package can't be changed until it syncs.`, transaction: t, lineItems: [], products: [], oldLineItem: null, selectedProduct: {} as VipProduct, diff: 0, paymentMethod: "check", checkNumber: "", miscNote: "", vpRefTxId: t.transactionNumber || "", currentPackage: "" });
        return;
      }
      if (vipDeal && addonDeal) {
        const addonLine = lineItems.find((li) =>
          li.dealId === addonDeal.dealId && li.quantity > 0 &&
          li.name.toLowerCase().includes("bronze") && !li.name.toLowerCase().startsWith("refund -"));
        setChangeLink({
          vip: vipDeal,
          addon: addonDeal,
          addonPkgName: addonLine?.name || "Bronze Package Add-On",
          addonPkgPrice: addonLine?.price ?? addonDeal.amount,
        });
      }
      // Use the fresh currentPackage from HubSpot to find the right old line item,
      // rather than guessing from line item ordering (which can be wrong after upgrades).
      const oldLineItem = vipDeal && addonDeal
        ? findPackageLineItem(lineItems.filter((li) => li.dealId === vipDeal.dealId))
        : currentPackage
        ? (lineItems.find((li) => isPackageLine(li) && li.name.toLowerCase().includes(currentPackage))
            || findPackageLineItem(lineItems))
        : findPackageLineItem(lineItems);
      // Store contact billing address for VersaPay AVS
      if (contactResp.success && (contactResp as any).contact) {
        const c = (contactResp as any).contact;
        vpContactAddressRef.current = {
          address1: c.address || "",
          city:     c.city    || "",
          state:    c.state   || "",
          zip:      c.zip     || "",
        };
      }
      setChangePkg({ status: "select", transaction: t, lineItems, products, oldLineItem, selectedProductId: "", currentPackage });
    } catch {
      setChangePkg({ status: "error", message: "Failed to load line items or products.", transaction: t, lineItems: [], products: [], oldLineItem: null, selectedProduct: {} as VipProduct, diff: 0, paymentMethod: "check", checkNumber: "", miscNote: "", vpRefTxId: t.transactionNumber || "", currentPackage: "" });
    }
  };

  const confirmChangePackage = () => {
    if (changePkg.status !== "select") return;
    const { transaction, lineItems, products, oldLineItem, selectedProductId, currentPackage } = changePkg;
    const selectedProduct = products.find((p) => p.product_id === selectedProductId);
    if (!selectedProduct) return;
    const oldPrice = packageBasis(lineItems, oldLineItem, changeLink);
    const diff = selectedProduct.price - oldPrice;
    // Map HubSpot payment_method label → dropdown value
    const PM_MAP: Record<string, string> = {
      "credit card": "credit_card",
      "check":       "check",
      "cash":        "cash",
      "miscellaneous": "miscellaneous",
    };
    const originalPm = PM_MAP[(transaction.paymentMethod || "").toLowerCase().trim()] || "check";
    // For upgrades default to credit_card; for downgrades default to the original payment method
    const defaultPaymentMethod = diff > 0 ? "credit_card" : originalPm;
    const refTx = changeLink ? changeLink.vip.transactionNumber : transaction.transactionNumber;
    setChangePkg({ status: "confirm", transaction, lineItems, products, oldLineItem, selectedProduct, diff, paymentMethod: defaultPaymentMethod, checkNumber: "", miscNote: "", vpRefTxId: refTx || "", currentPackage });
  };

  const submitChangePackage = async () => {
    if (changePkg.status !== "confirm") return;
    const { transaction, lineItems, products, oldLineItem, selectedProduct, diff, paymentMethod, checkNumber, miscNote, vpRefTxId, currentPackage } = changePkg;
    setChangePkg({ status: "processing", transaction, selectedProduct, diff });
    try {
      const resp = await changeAdminPackage({
        ...changeTarget(transaction, changeLink, diff, paymentMethod),
        oldLineItemId:     oldLineItem?.id,
        oldPackageName:    oldLineItem?.name || transaction.vipPackage,
        oldPackagePrice:   oldLineItem?.price ?? 0,
        promoReversal:     promoReversalFor(lineItems, oldLineItem),
        newProductId:      selectedProduct.product_id,
        newPackageName:    selectedProduct.name,
        newPackageTabKey:  selectedProduct.tabKey,
        newPackagePrice:   selectedProduct.price,
        diff,
        paymentMethod:     paymentMethod || undefined,
        transactionId:     diff < 0 && paymentMethod === "credit_card" ? vpRefTxId.trim() : undefined,
        checkNumber:       paymentMethod === "check" ? checkNumber : undefined,
        miscNote:          paymentMethod === "miscellaneous" ? miscNote : undefined,
      });
      if (!resp.success) {
        setChangePkg({ status: "error", message: resp.message || "Failed to change package.", transaction, lineItems, products, oldLineItem, selectedProduct, diff, paymentMethod, checkNumber, miscNote, vpRefTxId, currentPackage });
        return;
      }
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "package_change",
        label:   diff > 0 ? "Package Upgrade" : diff < 0 ? "Package Downgrade" : "Package Swap",
        details: `${transaction.dealName}${diff !== 0 ? ` (${diff > 0 ? "+" : ""}$${Math.abs(diff).toFixed(2)})` : ""}`,
        changes: [
          { field: "Package", oldValue: oldLineItem?.name || currentPackage || "?", newValue: selectedProduct.label },
          ...(diff !== 0 ? [{ field: "Amount", oldValue: `$${transaction.amountRaw.toFixed(2)}`, newValue: `$${(transaction.amountRaw + diff).toFixed(2)}` }] : []),
        ],
      });
      applyPackageChangeToRows(transaction, changeLink, selectedProduct.label, diff);
      setChangePkg({ status: "success", transaction, selectedProduct, diff });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "An error occurred.";
      setChangePkg({ status: "error", message: msg, transaction, lineItems, products, oldLineItem, selectedProduct, diff, paymentMethod, checkNumber, miscNote, vpRefTxId, currentPackage });
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: BRAND_GREEN, letterSpacing: "-0.3px" }}>Packages / Transactions</h4>
          <p className="mb-0" style={{ fontSize: "0.82rem", color: "#8a9199" }}>
            {loading
              ? "Loading…"
              : <><span style={{ fontWeight: 600, color: "#495057" }}>{sorted.length}</span> result{sorted.length !== 1 ? "s" : ""}{transactions.length !== sorted.length ? ` (filtered from ${transactions.length})` : ""}</>}
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <a
            href="/admin/bulk-upload"
            className="btn btn-sm btn-outline-secondary"
            style={{ borderRadius: 8, padding: "0.35rem 1rem", fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" }}
          >
            <i className="bi bi-cloud-upload me-1" />
            Order Upload
          </a>
          <a
            href="/admin/purchase-membership"
            className="btn btn-sm"
            style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none", borderRadius: 8, padding: "0.35rem 1rem", fontSize: "0.82rem", fontWeight: 500, textDecoration: "none" }}
          >
            <i className="bi bi-person-plus me-1" />
            Purchase Membership
          </a>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search name, email, GHIN, VIP card, transaction #…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 220, flex: "1 1 220px", maxWidth: 360, borderRadius: 8, border: "1px solid #dee2e6" }}
          />
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small fw-medium text-nowrap" style={{ color: "#6c757d" }}>From</label>
            <input type="date" className="form-control form-control-sm" style={{ width: 148, borderRadius: 8 }} value={startDate} max={endDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small fw-medium text-nowrap" style={{ color: "#6c757d" }}>To</label>
            <input type="date" className="form-control form-control-sm" style={{ width: 148, borderRadius: 8 }} value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small fw-medium text-nowrap" style={{ color: "#6c757d" }}>Card Type</label>
            <select className="form-select form-select-sm" style={{ width: 120, borderRadius: 8 }} value={cardTypeFilter} onChange={(e) => setCardTypeFilter(e.target.value as "" | "digital" | "paper")}>
              <option value="">All</option>
              <option value="digital">Digital</option>
              <option value="paper">Paper</option>
            </select>
          </div>
          <div className="d-flex align-items-center gap-1">
            <label className="form-label mb-0 small fw-medium text-nowrap" style={{ color: "#6c757d" }}>Stage</label>
            <select className="form-select form-select-sm" style={{ width: 175, borderRadius: 8 }} value={dealstage} onChange={(e) => setDealstage(e.target.value)} disabled={dealStages.length === 0}>
              <option value="all">All</option>
              {dealStages.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      {/* Table */}
      {!error && (
        <div style={{ borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
        <div className="table-responsive">
          <table
            className="table table-hover align-middle mb-0"
            style={{ fontSize: "0.84rem" }}
          >
            <thead>
              {(() => {
                const thBase: React.CSSProperties = { padding: "0.7rem 0.75rem", fontWeight: 600, fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.04em", color: "#6c757d", whiteSpace: "nowrap" };
                const thSort = (col: typeof sortCol): React.CSSProperties => ({ ...thBase, cursor: "pointer", userSelect: "none", color: sortCol === col ? BRAND_GREEN : "#6c757d" });
                const arrow = (col: typeof sortCol) => sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕";
                return (
                  <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                    <th style={{ ...thBase, cursor: "pointer", userSelect: "none", color: sortCol === null ? BRAND_GREEN : "#6c757d" }} onClick={() => { setSortCol(null); setCurrentPage(1); }}>#</th>
                    <th style={{ ...thSort("createDate"), textAlign: "center" }} onClick={() => toggleSort("createDate")} title="Date Created"><i className="bi bi-calendar3" /></th>
                    <th style={thBase}>Status</th>
                    <th style={thSort("name")} onClick={() => toggleSort("name")}>Name{arrow("name")}</th>
                    <th style={thBase}>Email</th>
                    <th style={thBase}>GHIN / VIP</th>
                    <th style={{ ...thSort("vipCardType"), textAlign: "center" }} onClick={() => toggleSort("vipCardType")}>VIP Card Type{arrow("vipCardType")}</th>
                    <th style={thSort("vipPackage")} onClick={() => toggleSort("vipPackage")}>Package / Add-ons{arrow("vipPackage")}</th>
                    <th style={thBase}>Amount</th>
                    <th style={thSort("paymentMethod")} onClick={() => toggleSort("paymentMethod")}>Payment{arrow("paymentMethod")}</th>
                    <th style={thBase}>Action</th>
                  </tr>
                );
              })()}
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={11} className="text-center py-4 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Loading transactions…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-4 text-muted">
                    {search
                      ? "No transactions match your search."
                      : "No transactions found for this date range."}
                  </td>
                </tr>
              )}
              {!loading &&
                paginated.map((t, i) => (
                  <tr key={t.dealId}>
                    <td className="text-nowrap">
                      {t.contactId ? (
                        <a
                          href={hubspotRecordUrl("contact", t.contactId)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none"
                          style={{ color: BRAND_GREEN, fontWeight: 600 }}
                        >
                          {(safePage - 1) * pageSize + i + 1}
                        </a>
                      ) : (safePage - 1) * pageSize + i + 1}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <span title={formatDateTime(t.createDate)} style={{ cursor: "default", color: "#6c757d", fontSize: "0.9rem" }}>
                        <i className="bi bi-calendar3" />
                      </span>
                    </td>
                    <td>{stageBadge(t.dealstage, dealStages)}</td>
                    <td className="text-nowrap">
                      {t.contactId ? (
                        <a
                          href={hubspotRecordUrl("contact", t.contactId)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none"
                          style={{ color: BRAND_GREEN, fontWeight: 500 }}
                        >
                          {t.firstName || t.lastName ? `${t.firstName} ${t.lastName}`.trim() : "—"}
                        </a>
                      ) : (
                        t.firstName || t.lastName ? `${t.firstName} ${t.lastName}`.trim() : "—"
                      )}
                      {t.createdSource === "Front-end" && (
                        <span title="Online registration" style={{ color: "#0d6efd", fontSize: "0.9rem", marginLeft: "0.4rem" }}>
                          <i className="bi bi-globe2" />
                        </span>
                      )}
                      {(t.createdSource === "Admin" || (!t.createdSource && t.isManual)) && (
                        <span
                          title={t.ownerName ? `Manually created by ${t.ownerName}` : "Manually created by admin"}
                          style={{ color: "#6c757d", fontSize: "0.9rem", marginLeft: "0.4rem" }}
                        >
                          <i className="bi bi-person-badge" />
                        </span>
                      )}
                      {t.createdSource === "Bulk Upload" && (
                        <span title="Bulk CSV upload" style={{ color: "#fd7e14", fontSize: "0.9rem", marginLeft: "0.4rem" }}>
                          <i className="bi bi-upload" />
                        </span>
                      )}
                    </td>
                    <td>
                      {t.email ? (
                        <a
                          href={`mailto:${t.email}`}
                          className="text-decoration-none"
                          style={{ color: BRAND_GREEN }}
                        >
                          {t.email}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="text-nowrap">
                      {t.ghinError && !t.isBagTagOnly && !t.isReplacementCard && !t.isWvVipCard && t.vipPackage !== "VIP" && (
                        <>
                          {t.ghinError !== "reprocess" && (
                            <button
                              onClick={() => handleEnrollWorkflow(t)}
                              disabled={enrollingIds.has(t.contactId)}
                              title="Re-enroll in GHIN workflow"
                              style={{ background: "none", border: "none", cursor: "pointer", color: "#fd7e14", padding: "0 0.15rem 0 0", fontSize: "0.85rem", lineHeight: 1, verticalAlign: "middle" }}
                            >
                              <i className={enrollingIds.has(t.contactId) ? "bi bi-hourglass-split" : "bi bi-arrow-clockwise"} />
                            </button>
                          )}
                          <span
                            title={t.ghinError}
                            style={{ color: "#fd7e14", fontSize: "0.85rem", marginRight: "0.3rem", cursor: "default", verticalAlign: "middle" }}
                          >
                            <i className="bi bi-info-circle-fill" />
                          </span>
                        </>
                      )}
                      {(t.ghinNumber && t.ghinNumber !== "-1") ? t.ghinNumber : "—"}
                      {t.vipCardNumber && (
                        <span className="text-muted" style={{ fontSize: "0.78rem" }}> / {t.vipCardNumber}</span>
                      )}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      {t.vipCardType?.toLowerCase() === "digital"
                        ? <i className="bi bi-phone-fill" title="Digital" style={{ fontSize: "1rem", color: "#212529" }} />
                        : t.vipCardType?.toLowerCase() === "paper"
                        ? <i className="bi bi-file-earmark-text-fill" title="Paper" style={{ fontSize: "1rem", color: "#495057" }} />
                        : t.vipCardType
                        ? t.vipCardType
                        : "—"}
                    </td>
                    <td>
                      {t.isWvVipCard
                        ? "WV VIP Card"
                        : t.isReplacementCard
                        ? "Replacement VIP Card"
                        : t.isBronzeAddon
                        ? "Bronze Add-On"
                        : t.isBagTagOnly
                        ? "Bag Tag"
                        : t.vipPackage || "—"}
                      {(t.packageChange === "Upgrade" || t.packageChange === "Downgrade") && (
                        <i
                          className={`bi ${t.packageChange === "Upgrade" ? "bi-arrow-up-circle-fill" : "bi-arrow-down-circle-fill"} ms-1`}
                          title={t.packageChange === "Upgrade" ? "Upgraded" : "Downgraded"}
                          aria-label={t.packageChange === "Upgrade" ? "Upgraded" : "Downgraded"}
                          style={{ color: t.packageChange === "Upgrade" ? "#198754" : "#fd7e14", fontSize: "0.9rem", verticalAlign: "-1px" }}
                        />
                      )}
                      {t.usedGiftCard && (
                        <button
                          type="button"
                          title="Gift card used — click for details"
                          onClick={() => openGcModal(t.dealId)}
                          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", marginLeft: "0.35rem", fontSize: "inherit", lineHeight: 1 }}
                        >🎁</button>
                      )}
                    </td>
                    <td className="fw-semibold">
                      {t.dealId ? (
                        <a
                          href={hubspotRecordUrl("deal", t.dealId)}
                          target="_blank"
                          rel="noreferrer"
                          className="text-decoration-none"
                          style={{ color: BRAND_GREEN }}
                        >
                          {t.amount || "—"}
                        </a>
                      ) : (t.amount || "—")}
                      {t.promoCode && (
                        <i
                          className="bi bi-tag-fill ms-1"
                          title={`Promo code ${t.promoCode} used${t.promoAmount ? ` (−$${t.promoAmount.toFixed(2)})` : ""}`}
                          aria-label={`Promo code ${t.promoCode} used`}
                          style={{ color: "#C9A24D", fontSize: "0.85rem", verticalAlign: "-1px" }}
                        />
                      )}
                    </td>
                    <td className="text-nowrap">{t.paymentMethod || "—"}</td>
                    <td className="text-nowrap">
                      <div className="d-flex align-items-center gap-1">
                        {t.contactId && (
                          <button
                            type="button"
                            title="Edit contact"
                            className="btn btn-sm"
                            style={{ backgroundColor: "rgba(2,56,34,0.08)", border: "none", color: BRAND_GREEN, fontSize: "0.82rem", padding: "0.2rem 0.5rem", borderRadius: 6 }}
                            onClick={() => openEditModal(t)}
                          >
                            <i className="bi bi-pencil-fill" />
                          </button>
                        )}
                        {(t.vipPackage || t.dealstage === HUBSPOT_IDS.partialRefundStage) && !t.isBagTagOnly && !t.isReplacementCard && t.netsuiteId && (
                          <button
                            type="button"
                            title="Change package"
                            className="btn btn-sm"
                            disabled={t.dealstage === HUBSPOT_IDS.partialRefundStage}
                            style={{ backgroundColor: "rgba(13,110,253,0.08)", border: "none", color: t.dealstage === HUBSPOT_IDS.partialRefundStage ? "#adb5bd" : "#0d6efd", fontSize: "0.82rem", padding: "0.2rem 0.5rem", borderRadius: 6, cursor: t.dealstage === HUBSPOT_IDS.partialRefundStage ? "default" : "pointer" }}
                            onClick={() => openChangeModal(t)}
                          >
                            <i className="bi bi-arrow-left-right" />
                          </button>
                        )}
                        {t.amountRaw > 0 && t.transactionNumber && t.netsuiteId && t.dealstage !== HUBSPOT_IDS.refundedStage && t.dealstage !== HUBSPOT_IDS.partialRefundStage && !refundedDealIds.has(t.dealId) && (t.paymentMethod || "").toLowerCase() !== "cash" && (
                          <button
                            type="button"
                            className="btn btn-sm"
                            style={{ backgroundColor: "rgba(220,53,69,0.08)", border: "none", color: "#b02a37", fontSize: "0.75rem", fontWeight: 600, padding: "0.2rem 0.65rem", borderRadius: 6 }}
                            onClick={() => openRefundModal(t)}
                          >
                            Refund
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Pagination */}
      {!error && !loading && filtered.length > 0 && (
        <div className="d-flex align-items-center justify-content-between flex-wrap mt-3" style={{ gap: "1.5rem" }}>
          <div />
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage === 1}
              onClick={() => setCurrentPage(1)}
            >«</button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - safePage) <= 2)
              .reduce<(number | "…")[]>((acc, p, i, arr) => {
                if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push("…");
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === "…" ? (
                  <span key={`ellipsis-${i}`} className="px-1 small text-muted">…</span>
                ) : (
                  <button
                    key={p}
                    type="button"
                    className="btn btn-sm"
                    style={{
                      minWidth: 34,
                      backgroundColor: p === safePage ? BRAND_GREEN : "transparent",
                      color: p === safePage ? "#fff" : "#495057",
                      border: `1px solid ${p === safePage ? BRAND_GREEN : "#dee2e6"}`,
                      fontWeight: p === safePage ? 600 : 400,
                    }}
                    onClick={() => setCurrentPage(p as number)}
                  >
                    {p}
                  </button>
                )
              )}
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >›</button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              disabled={safePage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >»</button>
            </div>
            <select
              className="form-select form-select-sm"
              style={{ width: "auto", paddingRight: "2rem" }}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value) as 20 | 50 | 100)}
            >
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
            </select>
          </div>
          <span className="small text-muted" style={{ whiteSpace: "nowrap" }}>
            {filtered.length} transaction{filtered.length !== 1 ? "s" : ""} — page {safePage} of {totalPages}
          </span>
        </div>
      )}

      {/* Edit contact modal */}
      {editModal.open && (
        <>
          <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.45)", zIndex: 1040 }} onClick={editModal.saving ? undefined : closeEditModal} />
          <div className="position-fixed top-50 start-50 translate-middle" style={{ width: "100%", maxWidth: 820, zIndex: 1050, padding: "0 1rem", maxHeight: "90vh", overflowY: "auto" }}>
            <div className="card shadow border-0">
              {/* Header */}
              <div className="px-4 py-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: BRAND_GREEN, borderTopLeftRadius: 6, borderTopRightRadius: 6 }}>
                <div className="d-flex align-items-center gap-2">
                  <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>Contact</span>
                  {editModal.isActiveCurrentYear && (
                    <span style={{ backgroundColor: "#22c55e", color: "#fff", fontSize: "0.7rem", fontWeight: 700, padding: "0.15rem 0.55rem", borderRadius: 20, letterSpacing: "0.03em" }}>
                      Active {editModal.membershipYear || new Date().getFullYear()}
                    </span>
                  )}
                </div>
                {!editModal.saving && <button type="button" className="btn-close btn-close-white btn-sm" onClick={closeEditModal} />}
              </div>

              {/* Tabs */}
              <ul className="nav nav-tabs px-4 pt-3" style={{ borderBottom: "1px solid #dee2e6" }}>
                {(["personal", "address", "history", "netsuite"] as const).map((tab) => (
                  <li className="nav-item" key={tab}>
                    <button
                      type="button"
                      className={`nav-link py-2 px-3 ${editModal.tab === tab ? "active" : ""}`}
                      style={{ fontSize: "0.82rem", fontWeight: editModal.tab === tab ? 600 : 400 }}
                      onClick={() => switchTab(tab)}
                    >
                      {tab === "personal" ? "Personal Information" : tab === "address" ? "Address Information" : tab === "history" ? "Purchase History" : "NetSuite"}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="card-body p-4">
                {/* ── Personal Information tab ── */}
                {editModal.tab === "personal" && (
                  editModal.loadingContact ? (
                    <div className="text-center py-4 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status" />Loading…
                    </div>
                  ) : editModal.success ? (
                    <div className="text-center py-2">
                      <div className="text-success fw-semibold mb-3">✓ Contact updated successfully</div>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeEditModal}>Close</button>
                    </div>
                  ) : (
                    <>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">First Name</label>
                          <input className="form-control form-control-sm" value={editModal.form.firstName} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, firstName: e.target.value } }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Last Name</label>
                          <input className="form-control form-control-sm" value={editModal.form.lastName} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, lastName: e.target.value } }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Email</label>
                          <input type="email" className="form-control form-control-sm" value={editModal.form.email} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, email: e.target.value } }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Phone</label>
                          <input className="form-control form-control-sm" value={editModal.form.phone} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, phone: e.target.value } }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Date of Birth</label>
                          <input type="date" className="form-control form-control-sm" value={editModal.form.date_of_birth} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, date_of_birth: e.target.value } }))} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Gender</label>
                          <select className="form-select form-select-sm" value={editModal.form.sex} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, sex: e.target.value } }))}>
                            <option value="">Select…</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">VIP Card Type</label>
                          <select className="form-select form-select-sm" value={editModal.form.vipCardType} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, vipCardType: e.target.value } }))}>
                            <option value="">Select…</option>
                            <option value="Digital">Digital</option>
                            {editModal.transaction?.vipPackage !== "Bronze Under 25" && <option value="Paper">Paper</option>}
                          </select>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">GHIN Number</label>
                          <input className="form-control form-control-sm" value={editModal.form.ghinNumber} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, ghinNumber: e.target.value } }))} placeholder="e.g. 1234567" />
                        </div>
                      </div>
                      {editModal.error && <div className="alert alert-danger small mt-3 mb-0">{editModal.error}</div>}
                      <div className="d-flex gap-2 justify-content-end mt-4">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeEditModal} disabled={editModal.saving}>Cancel</button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none", minWidth: 90 }}
                          onClick={saveEditModal}
                          disabled={editModal.saving}
                        >
                          {editModal.saving ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Saving…</> : "Save"}
                        </button>
                      </div>
                    </>
                  )
                )}

                {/* ── Address Information tab ── */}
                {editModal.tab === "address" && (
                  editModal.loadingContact ? (
                    <div className="text-center py-4 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status" />Loading…
                    </div>
                  ) : editModal.success ? (
                    <div className="text-center py-2">
                      <div className="text-success fw-semibold mb-3">✓ Contact updated successfully</div>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeEditModal}>Close</button>
                    </div>
                  ) : (
                    <>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label small fw-semibold">Address</label>
                          <input className="form-control form-control-sm" value={editModal.form.address} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, address: e.target.value } }))} />
                        </div>
                        <div className="col-12">
                          <label className="form-label small fw-semibold">Address Line 2</label>
                          <input className="form-control form-control-sm" value={editModal.form.street_address_2} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, street_address_2: e.target.value } }))} />
                        </div>
                        <div className="col-md-5">
                          <label className="form-label small fw-semibold">City</label>
                          <input className="form-control form-control-sm" value={editModal.form.city} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, city: e.target.value } }))} />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label small fw-semibold">State</label>
                          <select className="form-select form-select-sm" value={editModal.form.state} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, state: e.target.value } }))}>
                            <option value="">Select…</option>
                            {US_STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                          </select>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label small fw-semibold">ZIP</label>
                          <input className="form-control form-control-sm" value={editModal.form.zip} onChange={(e) => setEditModal((p) => ({ ...p, form: { ...p.form, zip: e.target.value } }))} />
                        </div>
                      </div>
                      {editModal.error && <div className="alert alert-danger small mt-3 mb-0">{editModal.error}</div>}
                      <div className="d-flex gap-2 justify-content-end mt-4">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeEditModal} disabled={editModal.saving}>Cancel</button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ backgroundColor: BRAND_GREEN, color: "#fff", border: "none", minWidth: 90 }}
                          onClick={saveEditModal}
                          disabled={editModal.saving}
                        >
                          {editModal.saving ? <><span className="spinner-border spinner-border-sm me-1" role="status" />Saving…</> : "Save"}
                        </button>
                      </div>
                    </>
                  )
                )}

                {/* ── Purchase History tab ── */}
                {editModal.tab === "history" && (
                  editModal.loadingDeals ? (
                    <div className="text-center py-4 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status" />Loading deals…
                    </div>
                  ) : editModal.dealsError ? (
                    <div className="alert alert-danger small">{editModal.dealsError}</div>
                  ) : editModal.deals.length === 0 ? (
                    <div className="text-muted small text-center py-3">No deals found for this contact.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.8rem" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                            <th className="text-uppercase small text-muted fw-semibold">Deal</th>
                            <th className="text-uppercase small text-muted fw-semibold">Transaction #</th>
                            <th className="text-uppercase small text-muted fw-semibold">Included Items</th>
                            <th className="text-uppercase small text-muted fw-semibold text-center">Card Type</th>
                            <th className="text-uppercase small text-muted fw-semibold">Amount</th>
                            <th className="text-uppercase small text-muted fw-semibold">Status</th>
                            <th className="text-uppercase small text-muted fw-semibold">Payment</th>
                            <th className="text-uppercase small text-muted fw-semibold">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editModal.deals.map((d) => {
                            const txRef = d.transactionNumber
                              ? d.transactionNumber
                              : d.paymentMethod?.toLowerCase() === "check" && d.checkNumber
                              ? `#${d.checkNumber}`
                              : d.paymentMethod?.toLowerCase() === "miscellaneous" && d.miscNote
                              ? d.miscNote
                              : null;
                            return (
                              <tr key={d.dealId}>
                                <td>
                                  <a
                                    href={hubspotRecordUrl("deal", d.dealId)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-decoration-none"
                                    style={{ color: BRAND_GREEN, fontWeight: 500 }}
                                  >
                                    {d.dealName}
                                  </a>
                                </td>
                                <td style={{ fontFamily: "monospace", fontSize: "0.78rem", color: "#495057", whiteSpace: "nowrap" }}>
                                  {txRef || <span className="text-muted">—</span>}
                                </td>
                                <td>
                                  <div className="d-flex flex-wrap gap-2" style={{ fontSize: "1rem" }}>
                                    {d.isWvVipCard       && <i className="bi bi-credit-card-fill" title="WV VIP Card"      style={{ color: BRAND_GREEN }} />}
                                    {d.isReplacementCard && <i className="bi bi-credit-card-fill" title="Replacement Card" style={{ color: "#0d6efd" }} />}
                                    {d.vipPackage && !d.isBronzeAddon && !d.isBagTagOnly && !d.isReplacementCard && !d.isWvVipCard && (
                                      <i className="bi bi-box-fill" title={d.vipPackage} style={{ color: BRAND_GREEN }} />
                                    )}
                                    {d.isBronzeAddon     && <i className="bi bi-plus-circle-fill" title="Bronze Add-On"   style={{ color: BRAND_GOLD }} />}
                                    {d.isBagTagOnly      && <i className="bi bi-tag-fill"          title="Bag Tag"         style={{ color: "#0d6efd" }} />}
                                    {d.hasBagTag && !d.isBagTagOnly && <i className="bi bi-tag-fill" title="Bag Tag"       style={{ color: "#d97706" }} />}
                                    {d.hasDonation       && <i className="bi bi-currency-dollar"   title="Donation"        style={{ color: "#198754" }} />}
                                    {!d.isWvVipCard && !d.isReplacementCard && !d.isBronzeAddon && !d.isBagTagOnly && !d.vipPackage && !d.hasBagTag && !d.hasDonation && (
                                      <span className="text-muted">—</span>
                                    )}
                                  </div>
                                </td>
                                <td className="text-center" style={{ minWidth: 110 }}>
                                  {cardTypeEditing === d.dealId ? (
                                    <div className="d-flex align-items-center justify-content-center gap-1">
                                      {cardTypeSaving === d.dealId ? (
                                        <span className="spinner-border spinner-border-sm text-muted" role="status" />
                                      ) : (
                                        <>
                                          <button
                                            type="button"
                                            className="btn btn-sm"
                                            style={{ fontSize: "0.72rem", padding: "0.15rem 0.5rem", backgroundColor: d.vipCardType?.toLowerCase() === "digital" ? BRAND_GREEN : "#e9ecef", color: d.vipCardType?.toLowerCase() === "digital" ? "#fff" : "#495057", border: "none", borderRadius: 4 }}
                                            onClick={() => saveCardType(d.dealId, "Digital")}
                                          >
                                            <i className="bi bi-phone-fill me-1" />Digital
                                          </button>
                                          {d.vipPackage !== "Bronze Under 25" && (
                                          <button
                                            type="button"
                                            className="btn btn-sm"
                                            style={{ fontSize: "0.72rem", padding: "0.15rem 0.5rem", backgroundColor: d.vipCardType?.toLowerCase() === "paper" ? BRAND_GREEN : "#e9ecef", color: d.vipCardType?.toLowerCase() === "paper" ? "#fff" : "#495057", border: "none", borderRadius: 4 }}
                                            onClick={() => saveCardType(d.dealId, "Paper")}
                                          >
                                            <i className="bi bi-file-earmark-text-fill me-1" />Paper
                                          </button>
                                          )}
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-link p-0 text-muted"
                                            style={{ fontSize: "0.75rem" }}
                                            onClick={() => setCardTypeEditing(null)}
                                          >✕</button>
                                        </>
                                      )}
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      title="Click to change card type"
                                      onClick={() => setCardTypeEditing(d.dealId)}
                                      style={{ background: "none", border: "none", cursor: "pointer", padding: "0.1rem 0.3rem", borderRadius: 4 }}
                                      className="d-inline-flex align-items-center gap-1"
                                    >
                                      {d.vipCardType?.toLowerCase() === "digital"
                                        ? <><i className="bi bi-phone-fill" style={{ fontSize: "0.95rem", color: "#212529" }} /></>
                                        : d.vipCardType?.toLowerCase() === "paper"
                                        ? <><i className="bi bi-file-earmark-text-fill" style={{ fontSize: "0.95rem", color: "#495057" }} /></>
                                        : <span className="text-muted small">—</span>}
                                      <i className="bi bi-pencil-fill" style={{ fontSize: "0.6rem", color: "#adb5bd" }} />
                                    </button>
                                  )}
                                </td>
                                <td className="fw-semibold">{d.amount}</td>
                                <td style={{ textAlign: "center" }}>
                                  {stageBadge(d.dealstage, dealStages)}
                                </td>
                                <td>{d.paymentMethod || "—"}</td>
                                <td className="text-muted text-nowrap">
                                  {d.createDate ? new Date(d.createDate).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" }) : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                )}

                {/* ── NetSuite tab ── */}
                {editModal.tab === "netsuite" && (
                  editModal.loadingDeals ? (
                    <div className="text-center py-4 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status" />Loading deals…
                    </div>
                  ) : editModal.dealsError ? (
                    <div className="alert alert-danger small">{editModal.dealsError}</div>
                  ) : !editModal.deals.some((d) => d.isVipPipeline) ? (
                    <div className="text-muted small text-center py-3">No VIP Card deals for this contact.</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.8rem" }}>
                        <thead>
                          <tr style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #e9ecef" }}>
                            <th className="text-uppercase small text-muted fw-semibold">Deal</th>
                            <th className="text-uppercase small text-muted fw-semibold">NetSuite ID</th>
                            <th className="text-uppercase small text-muted fw-semibold">NetSuite Credit Memo ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Donations and other non-VIP deals never go to NetSuite from this app */}
                          {editModal.deals.filter((d) => d.isVipPipeline).map((d) => (
                            <tr key={d.dealId} style={d.dealId === editModal.dealId ? { backgroundColor: "rgba(2,56,34,0.04)" } : undefined}>
                              <td>
                                <div className="fw-semibold">{d.dealName}</div>
                                <div className="text-muted" style={{ fontSize: "0.72rem" }}>{d.createDate ? formatDateTime(d.createDate) : ""}</div>
                              </td>
                              <td className="font-monospace">{d.netsuiteId || <span className="text-muted">Not synced yet</span>}</td>
                              <td className="font-monospace">{d.netsuiteCreditMemoId || <span className="text-muted">—</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Gift card modal */}
      {gcModal.open && (
        <>
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.45)", zIndex: 1040 }}
            onClick={closeGcModal}
          />
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{ width: "100%", maxWidth: 420, zIndex: 1050, padding: "0 1rem" }}
          >
            <div className="card shadow border-0">
              <div
                className="px-4 py-3 d-flex justify-content-between align-items-center"
                style={{ backgroundColor: BRAND_GREEN, borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
              >
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                  Gift Card / Promo Code
                </span>
                <button type="button" className="btn-close btn-close-white btn-sm" onClick={closeGcModal} />
              </div>
              <div className="card-body p-4">
                {gcModal.loading && (
                  <div className="text-center py-3 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Loading…
                  </div>
                )}
                {gcModal.error && (
                  <div className="alert alert-danger small mb-0">{gcModal.error}</div>
                )}
                {!gcModal.loading && !gcModal.error && gcModal.data && (
                  <div className="row gy-2 small">
                    <div className="col-5 text-muted fw-semibold">Promo Code</div>
                    <div className="col-7" style={{ fontFamily: "monospace", fontWeight: 600, fontSize: "0.9rem" }}>
                      {gcModal.data.promoCode || "—"}
                    </div>
                    <div className="col-5 text-muted fw-semibold">Value</div>
                    <div className="col-7 fw-semibold" style={{ color: BRAND_GREEN }}>
                      {gcModal.data.value || "—"}
                    </div>
                    <div className="col-5 text-muted fw-semibold">Status</div>
                    <div className="col-7">
                      <span style={{
                        display: "inline-block",
                        padding: "0.15rem 0.45rem",
                        borderRadius: 4,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        backgroundColor: gcModal.data.status === "Redeemed" ? "#dc3545" : gcModal.data.status === "Active" ? "#198754" : "#6c757d",
                        color: "#fff",
                      }}>
                        {gcModal.data.status || "—"}
                      </span>
                    </div>
                    <div className="col-5 text-muted fw-semibold">Type</div>
                    <div className="col-7">{gcModal.data.type || "—"}</div>
                    <div className="col-5 text-muted fw-semibold">Times Used</div>
                    <div className="col-7">{gcModal.data.timesUsed ?? "—"}</div>
                    <div className="col-5 text-muted fw-semibold">Created</div>
                    <div className="col-7">{gcModal.data.createdAt ? new Date(gcModal.data.createdAt).toLocaleDateString() : "—"}</div>
                  </div>
                )}
                {!gcModal.loading && !gcModal.error && !gcModal.data && (
                  <div className="text-muted small text-center py-2">No gift card found for this transaction.</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Refund modal */}
      {modalOpen && refundTransaction && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "rgba(0,0,0,0.45)", zIndex: 1040 }}
            onClick={refund.status === "processing" || refund.status === "loading" ? undefined : closeRefundModal}
          />

          {/* Dialog */}
          <div
            className="position-fixed top-50 start-50 translate-middle"
            style={{ width: "100%", maxWidth: 460, zIndex: 1050, padding: "0 1rem" }}
          >
            <div className="card shadow border-0">
              {/* Modal header */}
              <div
                className="px-4 py-3 d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: BRAND_GREEN,
                  borderTopLeftRadius: 6,
                  borderTopRightRadius: 6,
                }}
              >
                <span style={{ color: "#fff", fontWeight: 600, fontSize: "0.9rem" }}>
                  Process Refund
                </span>
                {refund.status !== "processing" && (
                  <button
                    type="button"
                    className="btn-close btn-close-white btn-sm"
                    onClick={closeRefundModal}
                  />
                )}
              </div>

              <div className="card-body p-4">
                {/* Customer + deal info */}
                <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa" }}>
                  <div className="small fw-semibold mb-1" style={{ color: BRAND_GREEN }}>
                    {refundTransaction.firstName} {refundTransaction.lastName}
                  </div>
                  <div className="small text-muted">{refundTransaction.dealName || "—"}</div>
                  <div className="small text-muted">
                    Order:{" "}
                    <span style={{ fontFamily: "monospace" }}>
                      {refundTransaction.orderNumber || refundTransaction.transactionNumber || "—"}
                    </span>
                  </div>
                </div>

                {/* Success state */}
                {refund.status === "success" && (
                  <div className="text-center py-2">
                    <div className="text-success fw-semibold mb-1">✓ Refund processed successfully</div>
                    {refund.refundId && (
                      <div className="small text-muted">
                        Refund ID:{" "}
                        <span style={{ fontFamily: "monospace" }}>{refund.refundId}</span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-3"
                      onClick={closeRefundModal}
                    >
                      Close
                    </button>
                  </div>
                )}

                {/* Error state */}
                {refund.status === "error" && (
                  <div className="text-center py-2">
                    <div className="text-danger fw-semibold mb-2">Refund failed</div>
                    <div className="small text-muted mb-3">{refund.message}</div>
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={closeRefundModal}
                      >
                        Close
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          if (refund.status === "error") {
                            setRefund({ status: "confirm", transaction: refund.transaction, lineItems: refund.lineItems, selected: refund.selected, currentPackage: refund.currentPackage });
                          }
                        }}
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                )}

                {/* Loading line items */}
                {refund.status === "loading" && (
                  <div className="text-center py-3 text-muted">
                    <div className="spinner-border spinner-border-sm me-2" role="status" />
                    Loading line items…
                  </div>
                )}

                {/* Confirm / processing / error state */}
                {(refund.status === "confirm" || refund.status === "processing" || refund.status === "error") && (() => {
                  const { lineItems, selected } = refund as { lineItems: LineItem[]; selected: Set<string> };
                  const isProcessing = refund.status === "processing";
                  return (
                    <>
                      <div className="mb-3">
                        <div className="form-label small fw-semibold mb-2">Select items to refund</div>
                        <div className="border rounded" style={{ overflow: "hidden" }}>
                          {(() => {
                            // Package keywords — names containing these are VIP package line items
                            const PKG_KEYWORDS = ["bronze", "silver", "gold", "wv vip", "vip card"];
                            const isPackageItem = (name: string) =>
                              PKG_KEYWORDS.some((k) => name.toLowerCase().includes(k));

                            // Net quantities by name — cancel out reversals
                            const netByName = new Map<string, { item: LineItem; netQty: number }>();
                            for (const item of lineItems) {
                              const key = `${item.dealId || ""}|${item.name.toLowerCase()}`;
                              const existing = netByName.get(key);
                              if (existing) {
                                existing.netQty += item.quantity;
                              } else {
                                netByName.set(key, { item, netQty: item.quantity });
                              }
                            }

                            const entries = Array.from(netByName.values());

                            // Use the fresh vip_card_package value fetched directly from HubSpot
                            // when the refund modal opened — avoids stale client-side state.
                            const currentPkg = ((refund.status === "confirm" || refund.status === "error" || refund.status === "processing") ? refund.currentPackage : "").toLowerCase().trim();

                            return entries
                              .filter(({ item, netQty }) => {
                                if (netQty <= 0) return false;     // fully reversed
                                if (item.price <= 0) return false; // $0 items not refundable
                                if (isPackageItem(item.name)) {
                                  // Only show the package that matches the deal's current package
                                  if (!currentPkg) return true;
                                  const name = item.name.toLowerCase();
                                  if (currentPkg.startsWith("bronze") && name.includes("bronze")) return true;
                                  return name.includes(currentPkg);
                                }
                                return true; // addon items always shown
                              })
                              // Use item.amount which the server already resolves to hs_post_tax_amount
                              // when tax was applied (e.g. bag tag 6%), so the refund total is correct.
                              .map(({ item }) => ({ ...item, quantity: 1, amount: item.amount }));
                          })().map((item, idx) => {
                            // Each deal must be in NetSuite before it can be refunded
                            const itemDeal = refundDeals?.find((d) => d.dealId === item.dealId);
                            const notInNetsuite = !!refundDeals && !itemDeal?.netsuiteId;
                            return (
                            <label
                              key={item.id}
                              className="d-flex align-items-center gap-3 px-3 py-2"
                              style={{
                                cursor: isProcessing || notInNetsuite ? "default" : "pointer",
                                opacity: notInNetsuite ? 0.55 : 1,
                                borderTop: idx > 0 ? "1px solid #f0f0f0" : undefined,
                                backgroundColor: selected.has(item.id) ? "rgba(2,56,34,0.04)" : "#fff",
                              }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input mt-0 flex-shrink-0"
                                checked={selected.has(item.id)}
                                onChange={() => toggleLineItem(item.id)}
                                disabled={isProcessing || notInNetsuite}
                              />
                              <span className="flex-grow-1 small">
                                {item.name}
                                {refundDeals && (
                                  <span className="d-block text-muted" style={{ fontSize: "0.7rem" }}>
                                    {itemDeal?.role === "addon" ? "Bronze Add-On deal" : "VIP deal"}
                                    {notInNetsuite && " · not in NetSuite yet"}
                                  </span>
                                )}
                              </span>
                              {item.quantity > 1 && (
                                <span className="text-muted small">×{item.quantity}</span>
                              )}
                              <span className="fw-semibold small text-nowrap">${item.amount.toFixed(2)}</span>
                            </label>
                            );
                          })}
                          {activePromoLines(lineItems).map((promo) => {
                            const included = withPromo(lineItems, selected).some((x) => x.id === promo.id);
                            return (
                              <div
                                key={promo.id}
                                className="d-flex align-items-center gap-3 px-3 py-2"
                                style={{ borderTop: "1px solid #f0f0f0", backgroundColor: included ? "rgba(2,56,34,0.04)" : "#fff" }}
                              >
                                <i className="bi bi-tag flex-shrink-0 text-muted" aria-hidden="true" />
                                <span className="flex-grow-1 small">
                                  {promo.name}
                                  <span className="d-block text-muted" style={{ fontSize: "0.7rem" }}>
                                    Comes off the refund when the package is refunded
                                  </span>
                                </span>
                                <span className={`fw-semibold small text-nowrap ${included ? "" : "text-muted"}`}>
                                  −${Math.abs(promo.amount).toFixed(2)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-3 px-1">
                        <span className="small text-muted">Refund total</span>
                        <span className="fw-semibold" style={{ fontSize: "1rem", color: selected.size > 0 ? "#dc3545" : "#adb5bd" }}>
                          ${effectiveRefundTotal.toFixed(2)}
                          {selectedTotal - effectiveRefundTotal > 0.005 && selected.size > 0 && (
                            <span className="text-muted small fw-normal ms-1" style={{ fontSize: "0.72rem" }}>(capped at amount paid)</span>
                          )}
                        </span>
                      </div>

                      {refund.status === "error" && (
                        <div className="alert alert-danger small py-2 mb-3">{refund.message}</div>
                      )}

                      <div className="d-flex gap-2 justify-content-end">
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={closeRefundModal} disabled={isProcessing}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ backgroundColor: !canSubmitRefund || isProcessing ? "#adb5bd" : "#dc3545", color: "#fff", border: "none", minWidth: 130 }}
                          onClick={submitRefund}
                          disabled={!canSubmitRefund || isProcessing}
                        >
                          {isProcessing ? (
                            <><span className="spinner-border spinner-border-sm me-1" role="status" />Processing…</>
                          ) : (
                            `Refund $${effectiveRefundTotal.toFixed(2)}`
                          )}
                        </button>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </>
      )}
      {/* ── Change Package modal ─────────────────────────────────────────── */}
      {changePkg.status !== "idle" && (() => {
        const cp = changePkg;
        const isProcessing = cp.status === "processing";
        const headerLabel = cp.status === "success"
          ? (cp.diff > 0 ? "Upgrade Complete" : cp.diff < 0 ? "Downgrade Complete" : "Package Changed")
          : "Change Package";

        // Computed values for select/confirm steps
        const selectProducts   = (cp.status === "select" || cp.status === "confirm" || cp.status === "error") ? cp.products : [];
        const selectedProduct  = (cp.status === "confirm" || cp.status === "success" || cp.status === "error") ? cp.selectedProduct : null;
        const oldLineItem      = (cp.status === "select" || cp.status === "confirm" || cp.status === "error") ? cp.oldLineItem : null;
        const diff             = (cp.status === "confirm" || cp.status === "processing" || cp.status === "success" || cp.status === "error") ? cp.diff : 0;
        const selectedProductId = cp.status === "select" ? cp.selectedProductId : "";
        const previewProduct   = cp.status === "select" ? selectProducts.find((p) => p.product_id === selectedProductId) : null;
        const previewDiff      = previewProduct && cp.status === "select" ? previewProduct.price - packageBasis(cp.lineItems, oldLineItem, changeLink) : null;
        const promoLine        = cp.status === "confirm" ? promoOnPackage(cp.lineItems, cp.oldLineItem) : null;
        // Identify the current product robustly using the stored HubSpot package key
        const currentPkg = (cp.status === "select" || cp.status === "confirm" || cp.status === "error")
          ? (cp.currentPackage || "").toLowerCase().trim()
          : "";
        const normPkg = (s: string) => s.toLowerCase().replace(/[-_]/g, " ").trim();
        const currentProduct = currentPkg
          ? selectProducts.find((p) => {
              const tabN = normPkg(p.tabKey);
              const pkgN = normPkg(currentPkg);
              return tabN === pkgN || normPkg(p.name).includes(pkgN) || pkgN.includes(tabN);
            })
          : null;

        return (
          <>
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ background: "rgba(0,0,0,0.45)", zIndex: 1060 }}
              onClick={() => !isProcessing && setChangePkg({ status: "idle" })} />
            <div className="position-fixed top-50 start-50 translate-middle" style={{ width: "100%", maxWidth: 500, zIndex: 1070, padding: "0 1rem" }}>
              <div className="card shadow border-0" style={{ borderRadius: 14 }}>

                {/* Header */}
                <div className="px-4 py-3 d-flex justify-content-between align-items-center"
                  style={{ backgroundColor: BRAND_GREEN, borderRadius: "14px 14px 0 0" }}>
                  <span style={{ color: BRAND_GOLD, fontWeight: 700, fontSize: "0.95rem", letterSpacing: "0.04em" }}>
                    <i className="bi bi-arrow-left-right me-2" />{headerLabel}
                  </span>
                  {!isProcessing && (
                    <button type="button" className="btn-close btn-close-white btn-sm" onClick={() => setChangePkg({ status: "idle" })} />
                  )}
                </div>

                <div className="card-body p-4">

                  {/* ── Loading ── */}
                  {cp.status === "loading" && (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm mb-2" style={{ color: BRAND_GREEN }} role="status" />
                      <p className="text-muted small mb-0">Loading packages…</p>
                    </div>
                  )}

                  {/* ── Select step ── */}
                  {cp.status === "select" && (
                    <>
                      {/* Current total */}
                      <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <div className="small text-muted fw-semibold">Current Package</div>
                            <div className="fw-semibold">{currentProduct?.label || oldLineItem?.name || cp.transaction.vipPackage || "—"}</div>
                          </div>
                          <div className="text-end">
                            <div className="small text-muted">Current total</div>
                            <div className="fw-semibold" style={{ color: BRAND_GREEN }}>${cp.transaction.amountRaw.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>

                      {/* New package selector */}
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">Select New Package</label>
                        <select className="form-select" value={selectedProductId}
                          onChange={(e) => setChangePkg({ ...cp, selectedProductId: e.target.value })}>
                          <option value="">Choose a package…</option>
                          {selectProducts.map((p) => (
                            <option key={p.product_id} value={p.product_id} disabled={p.product_id === currentProduct?.product_id}>
                              {p.label} — ${p.price.toFixed(2)}{p.product_id === currentProduct?.product_id ? " (current)" : ""}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Difference preview */}
                      {previewProduct && previewDiff !== null && (
                        <div className="p-3 rounded mb-3" style={{
                          backgroundColor: previewDiff > 0 ? "rgba(25,135,84,0.07)" : previewDiff < 0 ? "rgba(253,126,20,0.1)" : "rgba(108,117,125,0.08)",
                          border: `1px solid ${previewDiff > 0 ? "rgba(25,135,84,0.2)" : previewDiff < 0 ? "rgba(253,126,20,0.25)" : "#dee2e6"}`,
                        }}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="small text-muted">New total</span>
                            <span className="fw-semibold">${(cp.transaction.amountRaw + previewDiff).toFixed(2)}</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="small fw-semibold" style={{ color: previewDiff > 0 ? "#146c43" : previewDiff < 0 ? "#c96a00" : "#495057" }}>
                              {previewDiff > 0 ? "↑ Upgrade — customer owes" : previewDiff < 0 ? "↓ Downgrade — refund customer" : "→ Same price, package swap only"}
                            </span>
                            {previewDiff !== 0 && (
                              <span className="fw-bold" style={{ color: previewDiff > 0 ? "#146c43" : "#c96a00" }}>
                                ${Math.abs(previewDiff).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="d-flex gap-2 justify-content-end">
                        <button className="btn btn-sm btn-light" onClick={() => setChangePkg({ status: "idle" })}>Cancel</button>
                        <button className="btn btn-sm" disabled={!selectedProductId}
                          style={{ backgroundColor: BRAND_GREEN, color: "#fff", fontWeight: 600 }}
                          onClick={confirmChangePackage}>
                          Continue →
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── Confirm step ── */}
                  {cp.status === "confirm" && selectedProduct && (
                    <>
                      {/* Summary */}
                      <div className="mb-3 p-3 rounded" style={{ backgroundColor: "#f8f9fa", border: "1px solid #e9ecef" }}>
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">Current total ({currentProduct?.label || oldLineItem?.name || cp.transaction.vipPackage})</span>
                          <span className="fw-semibold">${cp.transaction.amountRaw.toFixed(2)}</span>
                        </div>
                        {promoLine && (
                          <div className="d-flex justify-content-between small mb-1">
                            <span className="text-muted">{promoLine.name.replace(/^promo code:\s*/i, "Promo ")} — original package only</span>
                            <span className="fw-semibold">+${Math.abs(promoLine.amount).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="d-flex justify-content-between small mb-1">
                          <span className="text-muted">New total ({selectedProduct.name})</span>
                          <span className="fw-semibold">${(cp.transaction.amountRaw + diff).toFixed(2)}</span>
                        </div>
                        <hr className="my-2" />
                        <div className="d-flex justify-content-between small">
                          <span className="fw-semibold">{diff > 0 ? "Amount to collect" : diff < 0 ? "Amount to refund" : "No payment needed"}</span>
                          {diff !== 0 && <span className="fw-bold" style={{ color: diff > 0 ? "#146c43" : "#c96a00" }}>${Math.abs(diff).toFixed(2)}</span>}
                        </div>
                      </div>

                      {/* Payment method */}
                      {diff !== 0 && (
                        <div className="mb-3">
                          <label className="form-label small fw-semibold">
                            {diff > 0 ? "Payment Method" : "Refund Method"}
                          </label>
                          <select className="form-select form-select-sm mb-2" value={cp.paymentMethod}
                            onChange={(e) => {
                              // Reset VP state when switching away from credit_card
                              if (e.target.value !== "credit_card") {
                                vpFrameRef.current = false;
                                vpSessionIdRef.current = "";
                                vpSessionAmtRef.current = 0;
                              }
                              setChangePkg({ ...cp, paymentMethod: e.target.value });
                            }}>
                            <option
                              value="credit_card"
                              disabled={diff < 0 && cp.transaction.paymentMethod?.toLowerCase().trim() !== "credit card"}>
                              Credit Card{diff < 0 && cp.transaction.paymentMethod?.toLowerCase().trim() !== "credit card" ? " (not original payment method)" : ""}
                            </option>
                            <option value="check">Check</option>
                            <option value="cash">Cash</option>
                            <option value="miscellaneous">Miscellaneous</option>
                          </select>
                          {cp.paymentMethod === "check" && (
                            <input type="text" className="form-control form-control-sm" placeholder="Check number"
                              value={cp.checkNumber} onChange={(e) => setChangePkg({ ...cp, checkNumber: e.target.value })} />
                          )}
                          {cp.paymentMethod === "miscellaneous" && (
                            <input type="text" className="form-control form-control-sm" placeholder="Note"
                              value={cp.miscNote} onChange={(e) => setChangePkg({ ...cp, miscNote: e.target.value })} />
                          )}

                          {/* VersaPay iframe — upgrades via credit card only */}
                          {diff > 0 && cp.paymentMethod === "credit_card" && (
                            <div className="mt-2">
                              {vpLoading && (
                                <div className="text-center py-3">
                                  <div className="spinner-border spinner-border-sm me-2" style={{ color: BRAND_GREEN }} role="status" />
                                  <span className="small text-muted">Loading payment form…</span>
                                </div>
                              )}
                              {vpError && (
                                <div className="alert alert-danger py-2 small mb-2">{vpError}</div>
                              )}
                              <div
                                ref={vpContainerRef}
                                style={{ minHeight: vpLoading ? 0 : 270, transition: "min-height 0.2s" }}
                              />
                              <button
                                type="button"
                                className="btn w-100 mt-2 fw-semibold"
                                style={{ backgroundColor: BRAND_GREEN, color: "#fff" }}
                                disabled={vpLoading || vpSubmitting || !vpClientRef.current}
                                onClick={async () => {
                                  try {
                                    setVpError(null);
                                    setVpSubmitting(true);
                                    await vpClientRef.current.submitEvents();
                                    growFrameWhenRecaptchaClicked(vpContainerRef.current?.querySelector("iframe"));
                                  } catch (err: any) {
                                    shrinkVpFrame();
                                    setVpError(err?.message || "Submission failed. Please try again.");
                                  } finally {
                                    setVpSubmitting(false);
                                  }
                                }}
                              >
                                {vpSubmitting
                                  ? <><span className="spinner-border spinner-border-sm me-2" />Processing…</>
                                  : <><i className="bi bi-lock-fill me-2" />Pay ${Math.abs(diff).toFixed(2)}</>}
                              </button>
                            </div>
                          )}

                          {/* Downgrade from VIP + Bronze Add-On: refund is split across both deals */}
                          {diff < 0 && cp.paymentMethod === "credit_card" && changeLink && (() => {
                            const { fromAddon, fromVip } = splitAddonRefund(diff, changeLink.addon.amount);
                            return (
                              <div className="mt-2 small">
                                {fromAddon > 0 && (isCardPaid(changeLink.addon) ? (
                                  <div style={{ color: "#c96a00" }}>
                                    <i className="bi bi-arrow-counterclockwise me-1" />
                                    ${fromAddon.toFixed(2)} will be refunded to the Bronze Add-On transaction <span className="font-monospace">{changeLink.addon.transactionNumber}</span>.
                                  </div>
                                ) : (
                                  <div className="text-danger">
                                    <i className="bi bi-exclamation-triangle me-1" />
                                    The Bronze Add-On was paid by {changeLink.addon.paymentMethod || "a non-card method"}, so ${fromAddon.toFixed(2)} can't go back to a card. Choose that refund method instead.
                                  </div>
                                ))}
                                {fromVip > 0 && (
                                  <div className="mt-2">
                                    <label className="form-label small fw-semibold mb-1">
                                      VIP deal VersaPay Transaction ID
                                      <span className="text-muted fw-normal ms-1">(${fromVip.toFixed(2)} refunded here)</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm font-monospace"
                                      placeholder="e.g. 1234567"
                                      value={cp.vpRefTxId}
                                      onChange={(e) => setChangePkg({ ...cp, vpRefTxId: e.target.value })}
                                    />
                                  </div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Downgrade + credit card: show editable transaction ref */}
                          {diff < 0 && cp.paymentMethod === "credit_card" && !changeLink && (
                            <div className="mt-2">
                              <label className="form-label small fw-semibold mb-1">
                                VersaPay Transaction ID
                                <span className="text-muted fw-normal ms-1">(from original payment)</span>
                              </label>
                              <input
                                type="text"
                                className="form-control form-control-sm font-monospace"
                                placeholder="e.g. 1234567"
                                value={cp.vpRefTxId}
                                onChange={(e) => setChangePkg({ ...cp, vpRefTxId: e.target.value })}
                              />
                              {!cp.vpRefTxId.trim() && (
                                <div className="text-danger small mt-1">
                                  <i className="bi bi-exclamation-triangle me-1" />
                                  Enter the VersaPay transaction ID to process the refund.
                                </div>
                              )}
                              {cp.vpRefTxId.trim() && (
                                <div className="small mt-1" style={{ color: "#c96a00" }}>
                                  <i className="bi bi-arrow-counterclockwise me-1" />
                                  ${Math.abs(diff).toFixed(2)} will be refunded to this transaction.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="d-flex gap-2 justify-content-end">
                        <button className="btn btn-sm btn-light"
                          onClick={() => setChangePkg({ status: "select", transaction: cp.transaction, lineItems: cp.lineItems, products: cp.products, oldLineItem: cp.oldLineItem, selectedProductId: cp.selectedProduct.product_id, currentPackage: cp.currentPackage })}>
                          ← Back
                        </button>
                        {/* For upgrades via credit card, VersaPay iframe handles submit — hide button.
                            For downgrades (any method) and non-CC upgrades, show confirm button. */}
                        {(diff <= 0 || cp.paymentMethod !== "credit_card") && (
                        <button
                          className="btn btn-sm fw-semibold"
                          style={{ backgroundColor: diff < 0 ? "#fd7e14" : BRAND_GREEN, color: "#fff" }}
                          disabled={diff < 0 && cp.paymentMethod === "credit_card" && (() => {
                            if (!changeLink) return !cp.vpRefTxId.trim();
                            const { fromAddon, fromVip } = splitAddonRefund(diff, changeLink.addon.amount);
                            return (fromAddon > 0 && !isCardPaid(changeLink.addon)) || (fromVip > 0 && !cp.vpRefTxId.trim());
                          })()}
                          onClick={submitChangePackage}>
                          {diff > 0 ? `Confirm & Collect $${Math.abs(diff).toFixed(2)}` : diff < 0 ? `Confirm & Refund $${Math.abs(diff).toFixed(2)}` : "Confirm Package Change"}
                        </button>
                        )}
                      </div>
                    </>
                  )}

                  {/* ── Processing ── */}
                  {cp.status === "processing" && (
                    <div className="text-center py-4">
                      <div className="spinner-border mb-2" style={{ color: BRAND_GREEN }} role="status" />
                      <p className="text-muted small mb-0">
                        {cp.diff < 0 ? "Processing refund and updating package…" : "Updating package…"}
                      </p>
                    </div>
                  )}

                  {/* ── Success ── */}
                  {cp.status === "success" && selectedProduct && (
                    <>
                      <div className="alert alert-success py-2 small mb-3">
                        <i className="bi bi-check-circle-fill me-1" />
                        Package updated to <strong>{selectedProduct.name}</strong>.
                        {cp.diff > 0 && <span> Collect <strong>${Math.abs(cp.diff).toFixed(2)}</strong> from the customer.</span>}
                        {cp.diff < 0 && <span> <strong>${Math.abs(cp.diff).toFixed(2)}</strong> refund processed via VersaPay.</span>}
                      </div>
                      <div className="text-end">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setChangePkg({ status: "idle" })}>Close</button>
                      </div>
                    </>
                  )}

                  {/* ── Error ── */}
                  {cp.status === "error" && (
                    <>
                      <div className="alert alert-danger py-2 small mb-3">{cp.message}</div>
                      <div className="d-flex gap-2 justify-content-end">
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setChangePkg({ status: "idle" })}>Cancel</button>
                        <button className="btn btn-sm btn-danger" onClick={() =>
                          setChangePkg({ status: "confirm", transaction: cp.transaction, lineItems: cp.lineItems, products: cp.products, oldLineItem: cp.oldLineItem, selectedProduct: cp.selectedProduct, diff: cp.diff, paymentMethod: cp.paymentMethod, checkNumber: cp.checkNumber, miscNote: cp.miscNote, vpRefTxId: cp.vpRefTxId, currentPackage: cp.currentPackage })}>
                          Try Again
                        </button>
                      </div>
                    </>
                  )}

                </div>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
};

export default AllTransactions;
